import {
  Calculator,
  ChevronDown,
  CheckCircle2,
  Download,
  Eye,
  FileText,
  HandCoins,
  Lock,
  Pencil,
  Plus,
  ReceiptText,
  RotateCcw,
  Save,
  Trash2,
  WalletCards,
  X,
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CustomDatePicker,
  CustomSelect,
  FeedbackMessage,
  ModalBackdrop,
  PaginationFooter,
} from '../common';
import {
  fetchSavingsPerKiloSetting,
  fetchTareAmountFeeSetting,
} from '../../services/masterSettingService';
import { fetchLoans } from '../../services/loanService';
import { fetchPortalContext, getCachedPortalContext } from '../../services/portalContextService';

const SACK_WEIGHT_KG = 0.5;
const SAVED_BALANCINGS_PAGE_SIZE = 5;
const BALANCING_INCLUDED_PO_STATUSES = new Set(['Received', 'Completed']);
const CUSTOM_PRICE_BASIS_VALUE = 'custom';
const AUTO_FILL_FIELD_NAMES = new Set([
  'grossKilo',
  'drySacks',
  'wetSacks',
  'rejectSacks',
  'giveaways',
  'tarePayment',
  'tareMultiplier',
]);
const PURCHASE_ORDER_SELECTION_AUTO_FIELD_NAMES = new Set([
  'grossKilo',
  'drySacks',
  'wetSacks',
  'rejectSacks',
  'giveaways',
]);

function PurchaseOrderSupplierBalancing({
  purchaseOrders = [],
  supplierOptions = [],
  balancingRecords = [],
  prefillSupplierId = '',
  focusBalancingRecordId = '',
  shouldFilterFocusedBalancingRecord = false,
  showHeaderIcon = false,
  isSaving = false,
  onEdit,
  onSave,
  onStatusChange,
  onRequestEditConfirmation,
  onRequestSaveConfirmation,
  onRequestStatusConfirmation,
}) {
  const [values, setValues] = useState(() => createInitialBalancingValues());
  const [manualAutoFields, setManualAutoFields] = useState({});
  const [tareAmountFee, setTareAmountFee] = useState(200);
  const [defaultSavingsPerKilo, setDefaultSavingsPerKilo] = useState(2);
  const [cashAdvanceRecords, setCashAdvanceRecords] = useState([]);
  const [cashAdvanceError, setCashAdvanceError] = useState('');
  const [selectedPurchaseOrderIds, setSelectedPurchaseOrderIds] = useState(new Set());
  const [expandedAllocationRows, setExpandedAllocationRows] = useState(new Set());
  const [balancingAllocations, setBalancingAllocations] = useState({});
  const includedPurchaseOrdersRef = useRef(null);
  const previousSelectionFilterKeyRef = useRef('');
  const pendingPrefillSupplierIdRef = useRef('');
  const manuallyChangedSavingsSupplierIdsRef = useRef(new Set());
  const isEditingBalancingRecordRef = useRef(false);
  const activeBalancingPurchaseOrderIds = useMemo(
    () => getActiveBalancingPurchaseOrderIds(balancingRecords),
    [balancingRecords]
  );
  const filteredPurchaseOrders = useMemo(
    () =>
      getFilteredPurchaseOrders(
        purchaseOrders,
        {
          dateFrom: values.dateFrom,
          dateTo: values.dateTo,
          supplierId: values.supplierId,
        },
        activeBalancingPurchaseOrderIds
      ),
    [
      activeBalancingPurchaseOrderIds,
      purchaseOrders,
      values.dateFrom,
      values.dateTo,
      values.supplierId,
    ]
  );
  const selectedPurchaseOrders = useMemo(
    () =>
      filteredPurchaseOrders.filter((purchaseOrder) =>
        selectedPurchaseOrderIds.has(String(purchaseOrder.id))
      ),
    [filteredPurchaseOrders, selectedPurchaseOrderIds]
  );
  const balancingAllocationLines = useMemo(
    () => buildBalancingAllocationLines(selectedPurchaseOrders, balancingAllocations),
    [balancingAllocations, selectedPurchaseOrders]
  );
  const balancingAllocationErrors = useMemo(
    () => validateBalancingAllocations(selectedPurchaseOrders, balancingAllocations),
    [balancingAllocations, selectedPurchaseOrders]
  );
  const computedInputs = useMemo(
    () => getComputedBalancingInputs(selectedPurchaseOrders),
    [selectedPurchaseOrders]
  );
  const selectedSupplierCashAdvanceTotal = useMemo(
    () => getSupplierOpenCashAdvanceTotal(cashAdvanceRecords, values.supplierId),
    [cashAdvanceRecords, values.supplierId]
  );
  const supplierIdsWithSavingsRecords = useMemo(
    () => getSupplierIdsWithSavingsRecords(balancingRecords),
    [balancingRecords]
  );
  const selectedSupplierHasSavingsRecords = values.supplierId
    ? supplierIdsWithSavingsRecords.has(String(values.supplierId))
    : false;
  const totals = useMemo(
    () =>
      getBalancingTotals(
        values,
        selectedPurchaseOrders,
        balancingAllocationLines,
        computedInputs,
        manualAutoFields,
        getTarePaymentFallback(values, tareAmountFee),
        selectedPurchaseOrders.length ? selectedSupplierCashAdvanceTotal : 0
      ),
    [
      computedInputs,
      balancingAllocationLines,
      manualAutoFields,
      selectedPurchaseOrders,
      selectedSupplierCashAdvanceTotal,
      tareAmountFee,
      values,
    ]
  );
  const hasWetSacks = toNumber(computedInputs.wetSacks) > 0;
  const hasRejectNetKilo = toNumber(computedInputs.rejectSacks) > 0;
  const isSavingsCapped = totals.savingsPotentialTotal - totals.savingsTotal > 0.009;
  const selectionFilterKey = `${values.supplierId}|${values.dateFrom}|${values.dateTo}`;
  const prioritizedSupplierOptions = useMemo(() => {
    const pendingCounts = getPendingBalancingPurchaseOrderCounts(
      purchaseOrders,
      activeBalancingPurchaseOrderIds
    );

    return supplierOptions
      .map((option) => {
        const pendingCount = pendingCounts.get(String(option.value)) || 0;

        return {
          ...option,
          pendingBalancingCount: pendingCount,
          badgeLabel: pendingCount > 0 ? `${pendingCount} pending` : '',
          badgeTone: pendingCount > 0 ? 'warning' : '',
          description:
            pendingCount > 0
              ? `${formatCount(pendingCount, 'P.O.')} pending balancing`
              : option.description,
        };
      })
      .sort((firstOption, secondOption) => {
        const firstPendingCount = Number(firstOption.pendingBalancingCount || 0);
        const secondPendingCount = Number(secondOption.pendingBalancingCount || 0);

        if (firstPendingCount !== secondPendingCount) {
          return secondPendingCount - firstPendingCount;
        }

        return String(firstOption.label || '').localeCompare(
          String(secondOption.label || ''),
          undefined,
          { sensitivity: 'base' }
        );
      });
  }, [activeBalancingPurchaseOrderIds, purchaseOrders, supplierOptions]);

  const loadCashAdvanceRecords = useCallback(async () => {
    setCashAdvanceError('');

    try {
      const records = await fetchLoans();

      setCashAdvanceRecords(Array.isArray(records) ? records : []);
      return true;
    } catch (error) {
      setCashAdvanceError(error.message);
      setCashAdvanceRecords([]);
      return false;
    }
  }, []);

  useEffect(() => {
    if (!prefillSupplierId) {
      return;
    }

    pendingPrefillSupplierIdRef.current = String(prefillSupplierId);
    setValues((currentValues) => ({
      ...currentValues,
      supplierId: String(prefillSupplierId),
    }));
    setManualAutoFields({});
  }, [prefillSupplierId]);

  useEffect(() => {
    const filteredPurchaseOrderIds = filteredPurchaseOrders.map((purchaseOrder) =>
      String(purchaseOrder.id)
    );
    const didFilterChange = previousSelectionFilterKeyRef.current !== selectionFilterKey;
    const shouldApplyPendingPrefill =
      pendingPrefillSupplierIdRef.current &&
      pendingPrefillSupplierIdRef.current === String(values.supplierId);

    previousSelectionFilterKeyRef.current = selectionFilterKey;

    setSelectedPurchaseOrderIds((currentIds) => {
      if (didFilterChange || shouldApplyPendingPrefill) {
        if (shouldApplyPendingPrefill && filteredPurchaseOrderIds.length > 0) {
          pendingPrefillSupplierIdRef.current = '';
        }

        return new Set(filteredPurchaseOrderIds);
      }

      const availableIds = new Set(filteredPurchaseOrderIds);

      return new Set([...currentIds].filter((purchaseOrderId) => availableIds.has(purchaseOrderId)));
    });
  }, [filteredPurchaseOrders, selectionFilterKey, values.supplierId]);

  useEffect(() => {
    let isMounted = true;

    async function loadMasterSettingDefaults() {
      try {
        const [tareSetting, savingsSetting] = await Promise.all([
          fetchTareAmountFeeSetting(),
          fetchSavingsPerKiloSetting(),
        ]);

        if (isMounted) {
          setTareAmountFee(Number(tareSetting.tareAmountFee || 200));
          setDefaultSavingsPerKilo(Number(savingsSetting.savingsPerKilo ?? 2));
        }
      } catch (error) {
        if (isMounted) {
          setTareAmountFee(200);
          setDefaultSavingsPerKilo(2);
        }
      }
    }

    loadMasterSettingDefaults();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    loadCashAdvanceRecords().then(() => {
      if (!isMounted) {
        return;
      }
    });

    return () => {
      isMounted = false;
    };
  }, [loadCashAdvanceRecords]);

  useEffect(() => {
    const supplierId = String(values.supplierId || '');

    if (
      !supplierId ||
      isEditingBalancingRecordRef.current ||
      !selectedSupplierHasSavingsRecords ||
      values.savingsEnabled ||
      manuallyChangedSavingsSupplierIdsRef.current.has(supplierId)
    ) {
      return;
    }

    setValues((currentValues) => {
      if (String(currentValues.supplierId || '') !== supplierId || currentValues.savingsEnabled) {
        return currentValues;
      }

      return {
        ...currentValues,
        savingsEnabled: true,
        savingsPerKilo: currentValues.savingsPerKilo || toInputString(defaultSavingsPerKilo),
      };
    });
  }, [
    defaultSavingsPerKilo,
    selectedSupplierHasSavingsRecords,
    values.savingsEnabled,
    values.supplierId,
  ]);

  function handleChange(event) {
    const { name, value } = event.target;

    if (name === 'supplierId') {
      isEditingBalancingRecordRef.current = false;
    }

    setValues((currentValues) => ({
      ...currentValues,
      [name]: sanitizeBalancingValue(name, value),
    }));

    if (AUTO_FILL_FIELD_NAMES.has(name)) {
      setManualAutoFields((currentFields) => ({
        ...currentFields,
        [name]: true,
      }));
    }
  }

  function handleSavingsEnabledChange(event) {
    const isEnabled = event.target.checked;
    const supplierId = String(values.supplierId || '');

    if (supplierId) {
      manuallyChangedSavingsSupplierIdsRef.current.add(supplierId);
    }

    setValues((currentValues) => ({
      ...currentValues,
      savingsEnabled: isEnabled,
      savingsPerKilo: isEnabled
        ? currentValues.savingsPerKilo || toInputString(defaultSavingsPerKilo)
        : '',
    }));
  }

  function handleReset() {
    isEditingBalancingRecordRef.current = false;
    manuallyChangedSavingsSupplierIdsRef.current = new Set();
    setValues(createInitialBalancingValues());
    setManualAutoFields({});
    setSelectedPurchaseOrderIds(new Set());
    setExpandedAllocationRows(new Set());
    setBalancingAllocations({});
  }

  function handlePurchaseOrderSelectionChange(purchaseOrderId, isSelected) {
    setSelectedPurchaseOrderIds((currentIds) => {
      const nextIds = new Set(currentIds);
      const normalizedId = String(purchaseOrderId);

      if (isSelected) {
        nextIds.add(normalizedId);
      } else {
        nextIds.delete(normalizedId);
        setExpandedAllocationRows((currentExpandedRows) => {
          const nextExpandedRows = new Set(currentExpandedRows);
          nextExpandedRows.delete(normalizedId);
          return nextExpandedRows;
        });
        setBalancingAllocations((currentAllocations) => removeObjectKey(currentAllocations, normalizedId));
      }

      return nextIds;
    });
  }

  function handleToggleAllPurchaseOrders(event) {
    if (event.target.checked) {
      setSelectedPurchaseOrderIds(
        new Set(filteredPurchaseOrders.map((purchaseOrder) => String(purchaseOrder.id)))
      );
      return;
    }

    setSelectedPurchaseOrderIds(new Set());
    setExpandedAllocationRows(new Set());
    setBalancingAllocations({});
  }

  function handleToggleAllocationRow(purchaseOrder) {
    const purchaseOrderId = String(purchaseOrder.id);

    if (!selectedPurchaseOrderIds.has(purchaseOrderId)) {
      return;
    }

    setExpandedAllocationRows((currentRows) => {
      const nextRows = new Set(currentRows);

      if (nextRows.has(purchaseOrderId)) {
        nextRows.delete(purchaseOrderId);
      } else {
        nextRows.add(purchaseOrderId);
        setBalancingAllocations((currentAllocations) =>
          currentAllocations[purchaseOrderId]
            ? currentAllocations
            : {
                ...currentAllocations,
                [purchaseOrderId]: [createDefaultBalancingAllocation(purchaseOrder)],
              }
        );
      }

      return nextRows;
    });
  }

  function handleAddAllocationLine(purchaseOrder) {
    const purchaseOrderId = String(purchaseOrder.id);

    setBalancingAllocations((currentAllocations) => {
      const currentLines = currentAllocations[purchaseOrderId] || [
        createDefaultBalancingAllocation(purchaseOrder),
      ];
      const remainingSacks = getRemainingAllocationSacks(purchaseOrder, currentLines);

      return {
        ...currentAllocations,
        [purchaseOrderId]: [
          ...currentLines,
          createDefaultBalancingAllocation(purchaseOrder, {
            sacks: remainingSacks > 0 ? remainingSacks : 0,
          }),
        ],
      };
    });
  }

  function handleAllocationLineChange(purchaseOrder, lineId, fieldName, value) {
    const purchaseOrderId = String(purchaseOrder.id);

    setBalancingAllocations((currentAllocations) => {
      const currentLines = currentAllocations[purchaseOrderId] || [
        createDefaultBalancingAllocation(purchaseOrder),
      ];

      return {
        ...currentAllocations,
        [purchaseOrderId]: currentLines.map((line) =>
          line.id === lineId
            ? normalizeBalancingAllocationLine(
                getNextBalancingAllocationLine(
                  line,
                  fieldName,
                  value,
                  purchaseOrder,
                  selectedPurchaseOrders
                )
              )
            : line
        ),
      };
    });
  }

  function handleRemoveAllocationLine(purchaseOrder, lineId) {
    const purchaseOrderId = String(purchaseOrder.id);

    setBalancingAllocations((currentAllocations) => {
      const currentLines = currentAllocations[purchaseOrderId] || [];
      const nextLines = currentLines.filter((line) => line.id !== lineId);

      return {
        ...currentAllocations,
        [purchaseOrderId]: nextLines.length
          ? nextLines
          : [createDefaultBalancingAllocation(purchaseOrder)],
      };
    });
  }

  function handleResetAllocationLines(purchaseOrder) {
    const purchaseOrderId = String(purchaseOrder.id);

    setBalancingAllocations((currentAllocations) => removeObjectKey(currentAllocations, purchaseOrderId));
    setExpandedAllocationRows((currentRows) => {
      const nextRows = new Set(currentRows);
      nextRows.delete(purchaseOrderId);
      return nextRows;
    });
  }

  async function handleSave() {
    if (!values.supplierId || !selectedPurchaseOrders.length || isSavingsCapped || balancingAllocationErrors.length) {
      return;
    }

    const didSave = await onSave?.({
      supplierId: Number(values.supplierId),
      dateFrom: values.dateFrom || null,
      dateTo: values.dateTo || null,
      purchaseOrderIds: selectedPurchaseOrders.map((purchaseOrder) => purchaseOrder.id),
      inputs: {
        pricePerKilo: getInputNumber(values.pricePerKilo, computedInputs.pricePerKilo),
        grossKilo: getAutoInputNumber('grossKilo', values, computedInputs.grossKilo, manualAutoFields),
        drySacks: getAutoInputNumber('drySacks', values, computedInputs.drySacks, manualAutoFields),
        wetSacks: getAutoInputNumber('wetSacks', values, computedInputs.wetSacks, manualAutoFields),
        rejectSacks: getAutoInputNumber(
          'rejectSacks',
          values,
          computedInputs.rejectSacks,
          manualAutoFields
        ),
        rejectAdjustmentKilo: toNumber(values.rejectAdjustmentKilo),
        wetAdjustmentPercent: toNumber(values.wetAdjustmentPercent),
        moistureContent: toNumber(computedInputs.moistureContent),
        giveaways: getAutoInputNumber('giveaways', values, totals.giveawaysDefault, manualAutoFields),
        karotPerSack: toNumber(values.karotPerSack),
        expenseAmPerSack: toNumber(values.expenseAmPerSack),
        expenseZcPerSack: toNumber(values.expenseZcPerSack),
        tareMultiplier: getAutoInputNumber(
          'tareMultiplier',
          values,
          selectedPurchaseOrders.length,
          manualAutoFields
        ),
        savingsEnabled: values.savingsEnabled ? 1 : 0,
        savingsPerKilo: values.savingsEnabled ? toNumber(values.savingsPerKilo) : 0,
        tarePayment: getAutoInputNumber(
          'tarePayment',
          values,
          getTarePaymentFallback(values, tareAmountFee),
          manualAutoFields
        ),
        balancingAllocations: serializeBalancingAllocations(balancingAllocationLines),
      },
      totals,
      status: 'For Payment',
    });

    if (didSave) {
      handleReset();
    }
  }

  function handleRequestSave() {
    if (!canSave || isSaving) {
      return;
    }

    if (onRequestSaveConfirmation) {
      onRequestSaveConfirmation(handleSave);
      return;
    }

    handleSave();
  }

  async function handleEditBalancing(record) {
    const didEdit = await onEdit?.(record);

    if (didEdit === false) {
      return false;
    }

    await loadCashAdvanceRecords();
    isEditingBalancingRecordRef.current = true;
    setValues(createBalancingValuesFromRecord(record));
    setManualAutoFields(createManualAutoFieldsFromRecord(record));
    const nextBalancingAllocations = createBalancingAllocationsFromRecord(record);
    const nextPurchaseOrders = purchaseOrders.filter((purchaseOrder) =>
      (record.purchaseOrderIds || []).some(
        (purchaseOrderId) => String(purchaseOrderId) === String(purchaseOrder.id)
      )
    );

    setBalancingAllocations(nextBalancingAllocations);
    setExpandedAllocationRows(
      new Set(
        Object.entries(nextBalancingAllocations)
          .filter(([purchaseOrderId, lines]) => {
            const purchaseOrder = nextPurchaseOrders.find(
              (item) => String(item.id) === String(purchaseOrderId)
            );

            return purchaseOrder && hasCustomBalancingAllocation(purchaseOrder, lines);
          })
          .map(([purchaseOrderId]) => purchaseOrderId)
      )
    );
    setSelectedPurchaseOrderIds(
      new Set((record.purchaseOrderIds || []).map((purchaseOrderId) => String(purchaseOrderId)))
    );

    window.setTimeout(() => {
      includedPurchaseOrdersRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);

    return true;
  }

  async function handleStatusChange(record, nextStatus) {
    const didUpdate = await onStatusChange?.(record, nextStatus);

    if (didUpdate !== false) {
      await loadCashAdvanceRecords();
    }

    return didUpdate;
  }

  const canSave = Boolean(
    values.supplierId &&
      selectedPurchaseOrders.length &&
      !isSavingsCapped &&
      !balancingAllocationErrors.length
  );
  const areAllPurchaseOrdersSelected =
    filteredPurchaseOrders.length > 0 &&
    selectedPurchaseOrderIds.size === filteredPurchaseOrders.length;
  const hasBalancingFilters = Boolean(values.supplierId || values.dateFrom || values.dateTo);

  return (
    <section className="w-full min-w-0 max-w-full overflow-hidden rounded-[24px] border border-[var(--theme-border)] bg-white shadow-[0_14px_34px_rgba(var(--theme-shadow-rgb),0.07)]">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[var(--theme-border-soft)] bg-white px-5 py-4 sm:px-6">
        <div className="flex min-w-0 items-start gap-3">
          {showHeaderIcon ? (
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-xl border border-[#d9e3f0] bg-white text-[#31446b] shadow-[0_12px_28px_rgba(15,23,42,0.06)]">
              <Calculator className="h-7 w-7" aria-hidden="true" />
            </span>
          ) : null}
          <div className="min-w-0">
            <h2 className="m-0 text-[1.22rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)]">
              Supplier Balancing
            </h2>
            <p className="m-0 mt-2 text-[0.92rem] text-[var(--theme-text-muted)]">
              Compute supplier payment from sacks, kilo, price, additions, and deductions before dumping.
            </p>
          </div>
        </div>
        <button
          className="inline-flex min-h-[38px] items-center justify-center gap-2 rounded-full border border-[var(--theme-border)] bg-white px-4 text-[0.82rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
          type="button"
          onClick={handleReset}
        >
          <RotateCcw className="h-4 w-4" aria-hidden="true" />
          Reset
        </button>
      </div>

      <div className="grid min-w-0 gap-4 p-3 sm:p-5 md:p-6">
        <div className="relative z-10 grid w-full min-w-0 max-w-full gap-3 rounded-[18px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-muted)] p-3 sm:p-4 md:grid-cols-3">
            <CustomSelect
              label="Supplier"
              name="supplierId"
              value={values.supplierId}
              onChange={handleChange}
              options={prioritizedSupplierOptions}
              placeholder="Select supplier"
            />
          <CustomDatePicker
            label="From P.O. Date"
            name="dateFrom"
            value={values.dateFrom}
            onChange={handleChange}
          />
          <CustomDatePicker
            label="To P.O. Date"
            name="dateTo"
            value={values.dateTo}
            onChange={handleChange}
          />
        </div>

        {hasBalancingFilters && !filteredPurchaseOrders.length ? (
          <FeedbackMessage
            tone="info"
            message="No purchase orders match this supplier and date range yet."
          />
        ) : null}

        <div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="grid min-w-0 gap-4">
            <section
              ref={includedPurchaseOrdersRef}
              className="min-w-0 scroll-mt-24 rounded-[18px] border border-[var(--theme-border)] bg-white p-3 sm:p-4"
            >
              <div className="mb-4 flex items-center gap-2">
                <FileText className="h-4 w-4 text-[var(--theme-text-subtle)]" aria-hidden="true" />
                <h3 className="m-0 text-[0.95rem] font-semibold text-[var(--theme-text-strong)]">
                  Included P.O. Sacks
                </h3>
              </div>
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-[700px] border-collapse">
                  <thead>
                    <tr className="border-b border-[var(--theme-border-soft)] text-left text-[0.7rem] font-semibold uppercase tracking-[0.06em] text-[var(--theme-text-muted)]">
                      <th className="px-3 py-2">
                        <input
                          className="h-4 w-4 rounded border-[var(--theme-border)] accent-[var(--theme-accent)] focus:ring-[var(--theme-accent)]"
                          type="checkbox"
                          checked={areAllPurchaseOrdersSelected}
                          onChange={handleToggleAllPurchaseOrders}
                          aria-label="Select or unselect all displayed purchase orders"
                        />
                      </th>
                      <th className="px-3 py-2">P.O. Code</th>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Marka</th>
                      <th className="px-3 py-2 text-right">MC</th>
                      <th className="px-3 py-2 text-right">Sacks</th>
                      <th className="px-3 py-2 text-right">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPurchaseOrders.length ? (
                      filteredPurchaseOrders.map((purchaseOrder) => {
                        const purchaseOrderId = String(purchaseOrder.id);
                        const isSelected = selectedPurchaseOrderIds.has(purchaseOrderId);
                        const isAllocationExpanded = expandedAllocationRows.has(purchaseOrderId);
                        const allocationLines = balancingAllocations[purchaseOrderId] || [];
                        const hasSplitAllocations = hasCustomBalancingAllocation(
                          purchaseOrder,
                          allocationLines
                        );

                        return (
                          <Fragment key={purchaseOrder.id}>
                            <tr
                              key={purchaseOrder.id}
                              className={`border-b text-[0.84rem] text-[var(--theme-text)] last:border-b-0 ${
                                isSelected && isAllocationExpanded
                                  ? 'border-[var(--theme-border)] bg-[var(--theme-accent-soft)]'
                                  : 'border-[var(--theme-border-soft)]'
                              }`}
                            >
                              <td className="px-3 py-2">
                                <input
                                  className="h-4 w-4 rounded border-[var(--theme-border)] accent-[var(--theme-accent)] focus:ring-[var(--theme-accent)]"
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(event) =>
                                    handlePurchaseOrderSelectionChange(
                                      purchaseOrder.id,
                                      event.target.checked
                                    )
                                  }
                                  aria-label={`Include ${formatPurchaseOrderCode(purchaseOrder)} in balancing`}
                                />
                              </td>
                              <td className="px-3 py-2 font-semibold text-[var(--theme-text-strong)]">
                                <span className="inline-flex items-center gap-2">
                                  <span>{formatPurchaseOrderCode(purchaseOrder)}</span>
                                  {hasSplitAllocations ? <SplitAllocationBadge /> : null}
                                </span>
                              </td>
                              <td className="px-3 py-2">{formatDate(purchaseOrder.poDate)}</td>
                              <td className="px-3 py-2">{purchaseOrder.sacksMark || '-'}</td>
                              <td className="px-3 py-2 text-right">
                                {formatPercent(getPurchaseOrderMoistureContent(purchaseOrder))}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <span>{formatNumber(getPurchaseOrderSacks(purchaseOrder), 0)}</span>
                                  <button
                                    className="inline-flex h-7 items-center gap-1 rounded-full border border-[var(--theme-border)] bg-white px-2 text-[0.7rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-45"
                                    type="button"
                                    onClick={() => handleToggleAllocationRow(purchaseOrder)}
                                    disabled={!isSelected}
                                    title="Split sacks across balancing prices"
                                  >
                                    <ChevronDown
                                      className={`h-3.5 w-3.5 transition ${isAllocationExpanded ? 'rotate-180' : ''}`}
                                      aria-hidden="true"
                                    />
                                    Split
                                  </button>
                                </div>
                              </td>
                              <td className="px-3 py-2 text-right">
                                {formatCurrency(purchaseOrder.actualPrice ?? purchaseOrder.price)}
                              </td>
                            </tr>
                            {isSelected && isAllocationExpanded ? (
                              <tr className="border-b border-[var(--theme-border)] bg-[var(--theme-accent-soft)]">
                                <td className="px-3 py-3" colSpan={7}>
                                  <BalancingAllocationEditor
                                    purchaseOrder={purchaseOrder}
                                    selectedPurchaseOrders={selectedPurchaseOrders}
                                    lines={allocationLines.length ? allocationLines : [createDefaultBalancingAllocation(purchaseOrder)]}
                                    onAddLine={() => handleAddAllocationLine(purchaseOrder)}
                                    onChangeLine={(lineId, fieldName, value) =>
                                      handleAllocationLineChange(purchaseOrder, lineId, fieldName, value)
                                    }
                                    onRemoveLine={(lineId) => handleRemoveAllocationLine(purchaseOrder, lineId)}
                                    onReset={() => handleResetAllocationLines(purchaseOrder)}
                                  />
                                </td>
                              </tr>
                            ) : null}
                          </Fragment>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          className="px-3 py-8 text-center text-[0.86rem] text-[var(--theme-text-muted)]"
                          colSpan={7}
                        >
                          Select a supplier or date range to prepare balancing.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {filteredPurchaseOrders.length ? (
                    <tfoot>
                      <tr className="border-t border-[var(--theme-border)] bg-[var(--theme-surface-muted)] text-[0.84rem] font-semibold text-[var(--theme-text-strong)]">
                        <td className="px-3 py-2" colSpan={4}>
                          Selected Total Sacks
                        </td>
                        <td className="px-3 py-2 text-right" />
                        <td className="px-3 py-2 text-right">
                          <HighlightedTotal>{formatNumber(totals.totalSacks, 0)}</HighlightedTotal>
                        </td>
                        <td className="px-3 py-2 text-right" />
                      </tr>
                    </tfoot>
                  ) : null}
                </table>
              </div>
              <div className="grid min-w-0 gap-2 md:hidden">
                {filteredPurchaseOrders.length ? (
                  <>
                    <label className="flex min-h-[42px] w-full min-w-0 items-center justify-between gap-3 rounded-[12px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-muted)] px-3 text-[0.82rem] font-semibold text-[var(--theme-text-subtle)]">
                      <span className="min-w-0">Select all displayed P.O.s</span>
                      <input
                        className="h-4 w-4 rounded border-[var(--theme-border)] accent-[var(--theme-accent)] focus:ring-[var(--theme-accent)]"
                        type="checkbox"
                        checked={areAllPurchaseOrdersSelected}
                        onChange={handleToggleAllPurchaseOrders}
                        aria-label="Select or unselect all displayed purchase orders"
                      />
                    </label>
                    {filteredPurchaseOrders.map((purchaseOrder) => {
                      const isSelected = selectedPurchaseOrderIds.has(String(purchaseOrder.id));

                      return (
                        <label
                          key={purchaseOrder.id}
                          className={`grid gap-3 rounded-[14px] border p-3 transition ${
                            isSelected
                              ? 'border-[var(--theme-accent)] bg-[var(--theme-accent-panel)]'
                              : 'border-[var(--theme-border-soft)] bg-white'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="truncate text-[0.88rem] font-semibold text-[var(--theme-text-strong)]">
                                {formatPurchaseOrderCode(purchaseOrder)}
                              </div>
                              <div className="mt-1 text-[0.78rem] text-[var(--theme-text-muted)]">
                                {formatDate(purchaseOrder.poDate)}
                              </div>
                            </div>
                            <input
                              className="mt-1 h-4 w-4 shrink-0 rounded border-[var(--theme-border)] accent-[var(--theme-accent)] focus:ring-[var(--theme-accent)]"
                              type="checkbox"
                              checked={isSelected}
                              onChange={(event) =>
                                handlePurchaseOrderSelectionChange(
                                  purchaseOrder.id,
                                  event.target.checked
                                )
                              }
                              aria-label={`Include ${formatPurchaseOrderCode(purchaseOrder)} in balancing`}
                            />
                          </div>
                          <div className="grid min-w-0 gap-2 text-[0.8rem] sm:grid-cols-2">
                            <MobileDetail label="Supplier" value={purchaseOrder.supplierName || '-'} />
                            <MobileDetail label="Marka" value={purchaseOrder.sacksMark || '-'} />
                            <MobileDetail
                              label="MC"
                              value={formatPercent(getPurchaseOrderMoistureContent(purchaseOrder))}
                            />
                            <MobileDetail
                              label="Sacks"
                              value={formatNumber(getPurchaseOrderSacks(purchaseOrder), 0)}
                            />
                            <MobileDetail
                              label="Price"
                              value={formatCurrency(purchaseOrder.actualPrice ?? purchaseOrder.price)}
                            />
                          </div>
                        </label>
                      );
                    })}
                    <div className="grid min-w-0 gap-2 rounded-[14px] border border-[var(--theme-border)] bg-[var(--theme-surface-muted)] p-3 text-[0.84rem] font-semibold text-[var(--theme-text-strong)]">
                      <div className="flex items-center justify-between gap-3">
                        <span>Selected Total Sacks</span>
                        <HighlightedTotal>{formatNumber(totals.totalSacks, 0)}</HighlightedTotal>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="rounded-[14px] border border-[var(--theme-border-soft)] bg-white px-3 py-8 text-center text-[0.86rem] text-[var(--theme-text-muted)]">
                    Select a supplier or date range to prepare balancing.
                  </div>
                )}
              </div>
              {balancingAllocationErrors.length ? (
                <div className="mt-3 grid gap-1 rounded-[12px] border border-[#ead7a8] bg-[#fff8e6] px-3 py-2 text-[0.78rem] font-semibold text-[#8a6424]">
                  {balancingAllocationErrors.map((message) => (
                    <span key={message}>{message}</span>
                  ))}
                </div>
              ) : null}
            </section>

            <section className="min-w-0 rounded-[18px] border border-[var(--theme-border)] bg-white p-3 sm:p-4">
              <div className="mb-4 flex items-center gap-2">
                <Calculator className="h-4 w-4 text-[var(--theme-text-subtle)]" aria-hidden="true" />
                <h3 className="m-0 text-[0.95rem] font-semibold text-[var(--theme-text-strong)]">
                  Weight and Adjustment Inputs
                </h3>
              </div>
              <div className="grid min-w-0 gap-3 md:grid-cols-3">
                <NumberInput
                  label="Gross Kilo"
                  name="grossKilo"
                  value={getAutoInputDisplayValue(
                    'grossKilo',
                    values,
                    computedInputs.grossKilo,
                    manualAutoFields
                  )}
                  onChange={handleChange}
                  formatWithCommas
                  displayOnly
                />
                <NumberInput
                  label="Dry Sacks"
                  name="drySacks"
                  value={getAutoInputDisplayValue(
                    'drySacks',
                    values,
                    computedInputs.drySacks,
                    manualAutoFields
                  )}
                  onChange={handleChange}
                  displayOnly
                />
                <NumberInput
                  label="Wet Sacks"
                  name="wetSacks"
                  value={getAutoInputDisplayValue(
                    'wetSacks',
                    values,
                    computedInputs.wetSacks,
                    manualAutoFields
                  )}
                  onChange={handleChange}
                  displayOnly
                />
                <NumberInput
                  label="Wet Adjustment (%)"
                  name="wetAdjustmentPercent"
                  value={values.wetAdjustmentPercent}
                  onChange={handleChange}
                  disabled={!hasWetSacks}
                />
                <NumberInput
                  label="Reject Net KG"
                  name="rejectSacks"
                  value={getAutoInputDisplayValue(
                    'rejectSacks',
                    values,
                    computedInputs.rejectSacks,
                    manualAutoFields
                  )}
                  onChange={handleChange}
                  displayOnly
                />
                <NumberInput
                  label="Reject Adjustment (kg)"
                  name="rejectAdjustmentKilo"
                  value={values.rejectAdjustmentKilo}
                  onChange={handleChange}
                  formatWithCommas
                  disabled={!hasRejectNetKilo}
                />
              </div>
            </section>

            <section className="min-w-0 rounded-[18px] border border-[var(--theme-border)] bg-white p-3 sm:p-4">
              <div className="mb-4 flex items-center gap-2">
                <WalletCards className="h-4 w-4 text-[var(--theme-text-subtle)]" aria-hidden="true" />
                <h3 className="m-0 text-[0.95rem] font-semibold text-[var(--theme-text-strong)]">
                  Payment Adjustments
                </h3>
              </div>
              <div className="grid min-w-0 gap-3 md:grid-cols-3">
                <MoneyInput
                  label="Giveaways"
                  name="giveaways"
                  value={
                    manualAutoFields.giveaways
                      ? values.giveaways
                      : formatFixedDecimalInputValue(
                          getAutoInputDisplayValue(
                            'giveaways',
                            values,
                            totals.giveawaysDefault,
                            manualAutoFields
                          ),
                          2
                        )
                  }
                  onChange={handleChange}
                  formatWithCommas
                />
                <MoneyInput
                  label="Karot per Sack"
                  name="karotPerSack"
                  value={values.karotPerSack}
                  onChange={handleChange}
                  formatWithCommas
                />
                <MoneyInput
                  label="Lansa Expense per Sack"
                  name="expenseAmPerSack"
                  value={values.expenseAmPerSack}
                  onChange={handleChange}
                  formatWithCommas
                />
                <MoneyInput
                  label="Expense ZC per Sack"
                  name="expenseZcPerSack"
                  value={values.expenseZcPerSack}
                  onChange={handleChange}
                />
                <NumberInput
                  label="Tare Multiplier"
                  name="tareMultiplier"
                  value={getAutoInputDisplayValue(
                    'tareMultiplier',
                    values,
                    selectedPurchaseOrders.length,
                    manualAutoFields
                  )}
                  onChange={handleChange}
                />
                <MoneyInput
                  label="Tare Payment"
                  name="tarePayment"
                  value={getAutoInputDisplayValue(
                    'tarePayment',
                    values,
                    getTarePaymentFallback(values, tareAmountFee),
                    manualAutoFields
                  )}
                  onChange={handleChange}
                  formatWithCommas
                />
              </div>
            </section>

            <section className="min-w-0 rounded-[18px] border border-[var(--theme-border)] bg-white p-3 sm:p-4">
              <div className="mb-4 flex items-center gap-2">
                <HandCoins className="h-4 w-4 text-[var(--theme-text-subtle)]" aria-hidden="true" />
                <h3 className="m-0 text-[0.95rem] font-semibold text-[var(--theme-text-strong)]">
                  Supplier's Cash Advance and Savings
                </h3>
              </div>
              {cashAdvanceError ? (
                <FeedbackMessage tone="error" message={cashAdvanceError} />
              ) : null}
              <div className="grid min-w-0 gap-3 md:grid-cols-2">
                <ReadOnlyMetric
                  label="Open C.A. Balance"
                  value={values.supplierId ? formatCurrency(selectedSupplierCashAdvanceTotal) : '-'}
                />
                <ReadOnlyMetric
                  label="C.A. Deducted This Balancing"
                  value={values.supplierId ? formatCurrency(totals.cashAdvanceDeductionTotal) : '-'}
                />
                <ReadOnlyMetric
                  label="Remaining C.A. After Balancing"
                  value={values.supplierId ? formatCurrency(totals.cashAdvanceRemainingAfterBalancing) : '-'}
                />
                <label className="flex min-h-[58px] cursor-pointer items-center justify-between gap-3 rounded-[14px] border border-[var(--theme-border)] bg-[var(--theme-surface-muted)] px-3 py-2">
                  <span className="min-w-0">
                    <span className="block text-[0.74rem] font-medium text-[var(--theme-text-subtle)]">
                      Enable Savings
                    </span>
                    <span className="mt-1 block text-[0.8rem] text-[var(--theme-text-muted)]">
                      Based on Dry Weight
                    </span>
                  </span>
                  <input
                    className="h-4 w-4 shrink-0 cursor-pointer rounded border-[var(--theme-border)] accent-[var(--theme-accent)] focus:ring-[var(--theme-accent)]"
                    type="checkbox"
                    checked={Boolean(values.savingsEnabled)}
                    onChange={handleSavingsEnabledChange}
                  />
                </label>
                {values.savingsEnabled ? (
                  <>
                    <MoneyInput
                      label="Savings per Kilo"
                      name="savingsPerKilo"
                      value={values.savingsPerKilo}
                      onChange={handleChange}
                      formatWithCommas
                    />
                    <ReadOnlyMetric
                      label="Savings Applied"
                      value={`${formatCurrency(totals.savingsTotal)} from ${formatNumber(totals.dryKilo)} kg`}
                    />
                    {isSavingsCapped ? (
                      <>
                        <ReadOnlyMetric
                          label="Savings Potential"
                          value={formatCurrency(totals.savingsPotentialTotal)}
                        />
                        <FeedbackMessage
                          tone="warning"
                          message="Savings is higher than the available payable amount. Lower the Savings per Kilo before saving."
                        />
                      </>
                    ) : null}
                  </>
                ) : null}
              </div>
            </section>
          </div>

          <aside className="h-fit min-w-0 rounded-[18px] border border-[var(--theme-accent)] bg-[var(--theme-accent-panel)] p-3 sm:p-4">
            <h3 className="m-0 text-[0.95rem] font-semibold text-[var(--theme-accent-panel-text)]">
              Balancing Summary
            </h3>
            <div className="mt-4 grid gap-2">
              <SummaryRow label="Total Sacks" value={formatNumber(totals.totalSacks, 0)} />
              <SummaryRow label="Gross Weight" value={`${formatNumber(totals.grossKilo)} kg`} />
              <SummaryRow label="Net Weight" value={`${formatNumber(totals.netKilo)} kg`} />
              <SummaryRow label="Dry Weight" value={`${formatNumber(totals.dryKilo)} kg`} />
              <SummaryRow label="Wet Share Weight" value={`${formatNumber(totals.wetShareKilo)} kg`} />
              <SummaryRow label="Wet Base Weight" value={`${formatNumber(totals.wetBaseKilo)} kg`} />
              <SummaryRow label="Wet Deduction" value={`${formatNumber(totals.wetDeductionKilo)} kg`} />
              <SummaryRow label="Wet Adjusted" value={`${formatNumber(totals.wetAdjustedKilo)} kg`} />
              <SummaryRow label="Reject Weight" value={`${formatNumber(totals.rejectKilo)} kg`} />
              <SummaryRow label="Reject Adjusted" value={`${formatNumber(totals.rejectAdjustedKilo)} kg`} />
              <SummaryRow label="Dry Pay" value={formatCurrency(totals.dryPay)} />
              <SummaryRow label="Wet Pay" value={formatCurrency(totals.wetPay)} />
              <SummaryRow label="Reject Pay" value={formatCurrency(totals.rejectPay)} />
              <SummaryRow
                label="Gross Payable"
                value={formatCurrency(totals.grossPayable)}
                tone="success"
                strong
              />
              <SummaryRow
                label="Giveaways (owner add-on)"
                value={formatCurrency(totals.giveawaysTotal)}
              />
              <SummaryRow
                label="Karot (owner add-on)"
                value={formatCurrency(totals.karotTotal)}
              />
              <SummaryRow label="Expense AM" value={formatCurrency(totals.expenseAmTotal)} />
              <SummaryRow label="Expense ZC" value={formatCurrency(totals.expenseZcTotal)} />
              <SummaryRow label="Supplier Savings" value={formatCurrency(totals.savingsTotal)} />
              {isSavingsCapped ? (
                <SummaryRow
                  label="Savings Over Limit"
                  value={formatCurrency(totals.savingsPotentialTotal - totals.savingsTotal)}
                  tone="warning"
                />
              ) : null}
              <SummaryRow label="Cash Advance" value={formatCurrency(totals.cashAdvanceDeductionTotal)} />
              {totals.cashAdvanceRemainingAfterBalancing > 0 ? (
                <SummaryRow
                  label="Remaining C.A."
                  value={formatCurrency(totals.cashAdvanceRemainingAfterBalancing)}
                />
              ) : null}
              <SummaryRow
                label="Deductions"
                value={formatCurrency(totals.totalDeductions)}
                tone="warning"
                strong
              />
            </div>
            <div className="mt-4 rounded-[14px] border border-white/60 bg-white px-4 py-3">
              <span className="block text-[0.72rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
                Total Balance
              </span>
              <strong className="mt-1 block text-[1.45rem] font-semibold tracking-[-0.03em] text-[var(--theme-text-strong)]">
                {formatCurrency(totals.totalBalance)}
              </strong>
            </div>
            <button
              className="mt-4 inline-flex min-h-[42px] w-full items-center justify-center gap-2 rounded-[14px] border border-[color-mix(in_srgb,var(--theme-accent-panel-text)_58%,transparent)] bg-[color-mix(in_srgb,var(--theme-accent-panel-text)_10%,transparent)] px-4 text-[0.88rem] font-semibold text-[var(--theme-accent-panel-text)] shadow-[0_10px_22px_rgba(var(--theme-shadow-rgb),0.18)] transition hover:border-[var(--theme-accent-panel-text)] hover:bg-[color-mix(in_srgb,var(--theme-accent-panel-text)_18%,transparent)] disabled:cursor-not-allowed disabled:border-[color-mix(in_srgb,var(--theme-accent-panel-text)_28%,transparent)] disabled:bg-[color-mix(in_srgb,var(--theme-accent-panel-text)_8%,transparent)] disabled:text-[color-mix(in_srgb,var(--theme-accent-panel-text)_64%,transparent)] disabled:shadow-none"
              type="button"
              onClick={handleRequestSave}
              disabled={!canSave || isSaving}
            >
              <Save className="h-4 w-4" aria-hidden="true" />
              {isSaving ? 'Saving...' : 'Save Balancing'}
            </button>
          </aside>
        </div>

        <SavedBalancings
          records={balancingRecords}
          purchaseOrders={purchaseOrders}
          focusRecordId={focusBalancingRecordId}
          shouldFilterFocusedRecord={shouldFilterFocusedBalancingRecord}
          isSaving={isSaving}
          onEdit={handleEditBalancing}
          onRequestEditConfirmation={onRequestEditConfirmation}
          onRequestStatusConfirmation={onRequestStatusConfirmation}
          onStatusChange={handleStatusChange}
        />
      </div>
    </section>
  );
}

function SavedBalancings({
  records,
  purchaseOrders = [],
  focusRecordId = '',
  shouldFilterFocusedRecord = false,
  isSaving,
  onEdit,
  onStatusChange,
  onRequestEditConfirmation,
  onRequestStatusConfirmation,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [supplierFilter, setSupplierFilter] = useState('');
  const [createdDateFilter, setCreatedDateFilter] = useState('');
  const [currentUser, setCurrentUser] = useState(() => getCachedPortalContext()?.user || null);
  const [previewRecord, setPreviewRecord] = useState(null);
  const [coveredPurchaseOrdersRecord, setCoveredPurchaseOrdersRecord] = useState(null);
  const savedBalancingsRef = useRef(null);
  const supplierFilterOptions = useMemo(() => {
    const optionsByValue = new Map();

    records.forEach((record) => {
      const supplierId = String(record.supplierId || '');

      if (!supplierId) {
        return;
      }

      optionsByValue.set(supplierId, {
        value: supplierId,
        label: formatSupplierDisplay(record.sacksMark, record.supplierName),
      });
    });

    return [
      { value: '', label: 'All Suppliers' },
      ...Array.from(optionsByValue.values()).sort((firstOption, secondOption) =>
        firstOption.label.localeCompare(secondOption.label, undefined, { sensitivity: 'base' })
      ),
    ];
  }, [records]);
  const filteredRecords = useMemo(
    () =>
      records
        .filter((record) => {
          const matchesSupplier =
            !supplierFilter || String(record.supplierId || '') === String(supplierFilter);
          const matchesCreatedDate =
            !createdDateFilter || toDateInputValue(record.createdAt) === createdDateFilter;

          return matchesSupplier && matchesCreatedDate;
        })
        .sort(compareSavedBalancingRecords),
    [createdDateFilter, records, supplierFilter]
  );
  const totalItems = filteredRecords.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / SAVED_BALANCINGS_PAGE_SIZE));
  const normalizedCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (normalizedCurrentPage - 1) * SAVED_BALANCINGS_PAGE_SIZE;
  const visibleRecords = filteredRecords.slice(startIndex, startIndex + SAVED_BALANCINGS_PAGE_SIZE);
  const startItem = totalItems ? startIndex + 1 : 0;
  const endItem = totalItems
    ? Math.min(startIndex + visibleRecords.length, totalItems)
    : 0;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    const normalizedFocusRecordId = String(focusRecordId || '').trim();

    if (!normalizedFocusRecordId) {
      return;
    }

    const targetRecord = records.find(
      (record) => String(record.id || '') === normalizedFocusRecordId
    );

    if (!targetRecord) {
      return;
    }

    const targetSupplierId = shouldFilterFocusedRecord
      ? String(targetRecord.supplierId || '')
      : '';

    if (supplierFilter !== targetSupplierId || createdDateFilter) {
      setSupplierFilter(targetSupplierId);
      setCreatedDateFilter('');
      setCurrentPage(1);
      return;
    }

    const targetRecordIndex = filteredRecords.findIndex(
      (record) => String(record.id || '') === normalizedFocusRecordId
    );

    if (targetRecordIndex < 0) {
      return;
    }

    setCurrentPage(Math.floor(targetRecordIndex / SAVED_BALANCINGS_PAGE_SIZE) + 1);
    window.setTimeout(() => {
      savedBalancingsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 120);
  }, [
    createdDateFilter,
    filteredRecords,
    focusRecordId,
    records,
    shouldFilterFocusedRecord,
    supplierFilter,
  ]);

  useEffect(() => {
    let isMounted = true;

    fetchPortalContext()
      .then((portalContext) => {
        if (isMounted) {
          setCurrentUser(portalContext?.user || null);
        }
      })
      .catch(() => {
        // Keep the cached user for the "You" label if the context refresh is slow or unavailable.
      });

    return () => {
      isMounted = false;
    };
  }, []);

  function handleSupplierFilterChange(event) {
    setSupplierFilter(event.target.value);
    setCurrentPage(1);
  }

  function handleCreatedDateFilterChange(event) {
    setCreatedDateFilter(event.target.value);
    setCurrentPage(1);
  }

  function handleStatusClick(record, nextStatus) {
    if (isBalancingLockedByCompletedPurchaseOrder(record, purchaseOrders)) {
      return;
    }

    const updateStatus = () => onStatusChange?.(record, nextStatus);

    if (onRequestStatusConfirmation) {
      onRequestStatusConfirmation(record, nextStatus, updateStatus);
      return;
    }

    updateStatus();
  }

  function handleEditClick(record) {
    if (isBalancingLockedByCompletedPurchaseOrder(record, purchaseOrders)) {
      return;
    }

    const editRecord = () => onEdit?.(record);

    if (onRequestEditConfirmation) {
      onRequestEditConfirmation(record, editRecord);
      return;
    }

    editRecord();
  }

  return (
    <section
      ref={savedBalancingsRef}
      className="w-full min-w-0 max-w-full scroll-mt-24 rounded-[18px] border border-[var(--theme-border)] bg-white p-3 sm:p-4"
    >
      <div className="relative z-20 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-2">
          <ReceiptText className="h-4 w-4 text-[var(--theme-text-subtle)]" aria-hidden="true" />
          <h3 className="m-0 text-[0.95rem] font-semibold text-[var(--theme-text-strong)]">
            Saved Balancing Records
          </h3>
        </div>
        <div className="grid w-full min-w-0 gap-2 sm:w-auto sm:grid-cols-[minmax(220px,260px)_auto] sm:items-center">
          <CustomSelect
            label="Supplier"
            name="savedBalancingSupplier"
            value={supplierFilter}
            onChange={handleSupplierFilterChange}
            options={supplierFilterOptions}
            searchable
            wrapperClassName="relative flex w-full min-w-0 flex-col gap-1.5 sm:flex-row sm:items-center"
            labelClassName="shrink-0 text-[0.74rem] font-medium text-[var(--theme-text-subtle)]"
            triggerShapeClassName="rounded-full"
          />
          <CustomDatePicker
            label="Created Date"
            name="savedBalancingCreatedDate"
            value={createdDateFilter}
            onChange={handleCreatedDateFilterChange}
            wrapperClassName="relative flex w-full min-w-0 flex-col gap-1.5 sm:w-auto sm:flex-row sm:items-center"
            labelClassName="shrink-0 text-[0.74rem] font-medium text-[var(--theme-text-subtle)]"
            triggerShapeClassName="rounded-full"
            triggerClassName="sm:w-[220px]"
            preferredPlacement="top"
            popupAlign="right"
          />
        </div>
      </div>
      <div className="mt-4 w-full min-w-0 max-w-full overflow-x-auto">
        <table className="w-full min-w-[940px] border-collapse">
          <thead>
            <tr className="border-b border-[var(--theme-border-soft)] text-left text-[0.7rem] font-semibold uppercase tracking-[0.06em] text-[var(--theme-text-muted)]">
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2">Supplier</th>
              <th className="px-3 py-2">Transacted By</th>
              <th className="px-3 py-2">Date Range</th>
              <th className="px-3 py-2 text-right">Sacks</th>
              <th className="px-3 py-2 text-right">Total Balance</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {visibleRecords.length ? (
              visibleRecords.map((record) => {
                const isFocusedRecord =
                  String(focusRecordId || '').trim() &&
                  String(record.id || '') === String(focusRecordId || '').trim();
                const isCapitalLocked = isBalancingLockedByCompletedPurchaseOrder(
                  record,
                  purchaseOrders
                );
                const shouldShowStatusAction =
                  record.status !== 'Cancelled' &&
                  !(record.status === 'Paid' && isCapitalLocked);
                const shouldShowEditAction =
                  record.status === 'For Payment' && !isCapitalLocked;

                return (
                  <tr
                    key={record.id}
                    className={`border-b border-[var(--theme-border-soft)] text-[0.84rem] text-[var(--theme-text)] transition-colors last:border-b-0 ${
                      isFocusedRecord ? 'bg-[color-mix(in_srgb,var(--theme-accent-soft)_72%,white)]' : ''
                    }`}
                  >
                    <td className="px-3 py-2">
                      <button
                        className="inline-flex min-h-[30px] items-center justify-start gap-2 rounded-full px-2 text-left text-[0.82rem] font-semibold text-[var(--theme-accent)] underline-offset-2 transition hover:bg-[var(--theme-surface-soft)] hover:text-[var(--theme-accent-strong)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)]"
                        type="button"
                        onClick={() => setCoveredPurchaseOrdersRecord(record)}
                        title="View covered P.O.s"
                      >
                        <span>{formatDate(record.createdAt)}</span>
                        <Eye size={13} strokeWidth={2.2} aria-hidden="true" />
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      {formatSupplierDisplay(record.sacksMark, record.supplierName)}
                    </td>
                    <td className="px-3 py-2">
                      {formatTransactedBy(record, currentUser)}
                    </td>
                    <td className="px-3 py-2">
                      {formatBalancingDateRange(record.dateFrom, record.dateTo)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {formatNumber(record.totals?.totalSacks, 0)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {formatCurrency(record.totals?.totalBalance)}
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge status={record.status} />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-2">
                        {isCapitalLocked ? <CapitalLockedBadge /> : null}
                        {record.status === 'Cancelled' ? (
                          <span className="text-[0.78rem] text-[var(--theme-text-muted)]">No action</span>
                        ) : shouldShowStatusAction ? (
                          <button
                            className="inline-flex min-h-[34px] items-center justify-center gap-1.5 rounded-full border border-[var(--theme-border)] bg-white px-3 text-[0.78rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-50"
                            type="button"
                            onClick={() =>
                              handleStatusClick(
                                record,
                                record.status === 'Paid' ? 'For Payment' : 'Paid'
                              )
                            }
                            disabled={isSaving}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                            {record.status === 'Paid' ? 'Reopen' : 'Mark Paid'}
                          </button>
                        ) : null}
                        {shouldShowEditAction ? (
                          <button
                            className="inline-flex min-h-[34px] items-center justify-center gap-1.5 rounded-full border border-[var(--theme-border)] bg-white px-3 text-[0.78rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-50"
                            type="button"
                            onClick={() => handleEditClick(record)}
                            disabled={isSaving}
                          >
                            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                            Edit
                          </button>
                        ) : null}
                        <button
                          className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-50"
                          type="button"
                          onClick={() => setPreviewRecord(record)}
                          title="Preview PDF"
                          aria-label={`Preview PDF for ${formatSupplierDisplay(record.sacksMark, record.supplierName)}`}
                          disabled={isSaving}
                        >
                          <Download className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  className="px-3 py-8 text-center text-[0.86rem] text-[var(--theme-text-muted)]"
                  colSpan={8}
                >
                  {records.length
                    ? 'No saved supplier balancing records match these filters.'
                    : 'No saved supplier balancing records yet.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-3 overflow-hidden rounded-[14px] border border-[var(--theme-border-soft)]">
        <PaginationFooter
          currentPage={normalizedCurrentPage}
          endItem={endItem}
          isNextDisabled={normalizedCurrentPage >= totalPages}
          isPreviousDisabled={normalizedCurrentPage <= 1}
          onNext={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
          onPrevious={() => setCurrentPage((page) => Math.max(page - 1, 1))}
          startItem={startItem}
          totalItems={totalItems}
          totalPages={totalPages}
        />
      </div>
      {previewRecord ? (
        <SupplierBalancingPdfPreviewModal
          record={previewRecord}
          purchaseOrders={getCoveredPurchaseOrders(previewRecord, purchaseOrders)}
          transactedBy={formatPdfTransactedBy(previewRecord)}
          onClose={() => setPreviewRecord(null)}
        />
      ) : null}
      {coveredPurchaseOrdersRecord ? (
        <CoveredPurchaseOrdersModal
          record={coveredPurchaseOrdersRecord}
          purchaseOrders={getCoveredPurchaseOrders(coveredPurchaseOrdersRecord, purchaseOrders)}
          onClose={() => setCoveredPurchaseOrdersRecord(null)}
        />
      ) : null}
    </section>
  );
}

function StatusBadge({ status }) {
  const isPaid = status === 'Paid';
  const isCancelled = status === 'Cancelled';

  return (
    <span
      className={`inline-flex min-h-[26px] items-center rounded-full border px-2.5 text-[0.7rem] font-semibold uppercase tracking-[0.06em] ${
        isPaid
          ? 'border-[#cfe4d1] bg-[#f1faf2] text-[#3f7545]'
          : isCancelled
            ? 'border-[#efd8d0] bg-[#fbf3f0] text-[#9a5448]'
            : 'border-[#ead7a8] bg-[#fff8e6] text-[#8a6424]'
      }`}
    >
      {status || 'For Payment'}
    </span>
  );
}

function CapitalLockedBadge() {
  return (
    <span
      className="inline-flex min-h-[34px] items-center justify-center gap-1.5 rounded-full border border-[#efc3b8] bg-[#fff6f3] px-3 text-[0.72rem] font-semibold uppercase tracking-[0.05em] text-[#a34334]"
      title="Locked because one or more included P.O.s are already Completed."
    >
      <WalletCards className="h-3.5 w-3.5" aria-hidden="true" />
      Capital Locked
    </span>
  );
}

function CoveredPurchaseOrdersModal({ record, purchaseOrders = [], onClose }) {
  return (
    <ModalBackdrop
      backdropClassName="bg-[rgba(33,27,22,0.28)] backdrop-blur-[3px]"
      contentClassName="!px-4"
      zIndexClassName="z-[70]"
    >
      <div className="w-full max-w-5xl overflow-hidden rounded-[24px] border border-[var(--theme-border-soft)] bg-white shadow-[0_28px_80px_rgba(var(--theme-shadow-rgb),0.24)]">
        <div className="flex items-start justify-between gap-4 border-b border-[var(--theme-border-soft)] bg-[var(--theme-surface-muted)] px-5 py-5 sm:px-6">
          <div className="flex min-w-0 items-start gap-3">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[var(--theme-accent)] text-white shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.24)]">
              <FileText className="h-5 w-5" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h3 className="m-0 text-[1.16rem] font-semibold text-[var(--theme-text)]">
                Covered P.O.s
              </h3>
              <p className="m-0 mt-1 text-[0.9rem] leading-5 text-[var(--theme-text-muted)]">
                {formatSupplierDisplay(record.sacksMark, record.supplierName)} / {formatDate(record.createdAt)}
              </p>
            </div>
          </div>
          <button
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onClose}
            aria-label="Close covered P.O.s"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="max-h-[68vh] overflow-auto p-4 sm:p-5">
          <div className="grid gap-3 sm:grid-cols-3">
            <CoveredPurchaseOrderMetric
              label="Included Orders"
              value={formatNumber(record.purchaseOrderIds?.length || 0, 0)}
            />
            <CoveredPurchaseOrderMetric
              label="Total Sacks"
              value={formatNumber(record.totals?.totalSacks, 0)}
            />
            <CoveredPurchaseOrderMetric
              label="Total Balance"
              value={formatCurrency(record.totals?.totalBalance)}
            />
          </div>

          <div className="mt-4 overflow-hidden rounded-[18px] border border-[var(--theme-border)]">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] border-collapse">
                <thead>
                  <tr className="bg-[var(--theme-accent-panel)] text-left text-[0.7rem] font-semibold uppercase tracking-[0.06em] text-[var(--theme-accent-panel-text)]">
                    <th className="px-3 py-3">P.O. Code</th>
                    <th className="px-3 py-3">Supplier / Sacks Mark</th>
                    <th className="px-3 py-3">P.O. Date</th>
                    <th className="px-3 py-3">Warehouse</th>
                    <th className="px-3 py-3 text-right">Sacks</th>
                    <th className="px-3 py-3 text-right">Price / Kilo</th>
                    <th className="px-3 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseOrders.length ? (
                    purchaseOrders.map((purchaseOrder) => (
                      <tr
                        key={purchaseOrder.id}
                        className="border-t border-[var(--theme-border-soft)] text-[0.84rem] text-[var(--theme-text)]"
                      >
                        <td className="px-3 py-3 font-semibold">
                          {formatPurchaseOrderCode(purchaseOrder)}
                        </td>
                        <td className="px-3 py-3">
                          {formatSupplierDisplay(
                            purchaseOrder.sacksMark,
                            purchaseOrder.supplierName
                          )}
                        </td>
                        <td className="px-3 py-3">{formatDate(purchaseOrder.poDate)}</td>
                        <td className="px-3 py-3">{purchaseOrder.warehouseLocation || '-'}</td>
                        <td className="px-3 py-3 text-right">
                          {formatNumber(getPurchaseOrderSacks(purchaseOrder), 0)}
                        </td>
                        <td className="px-3 py-3 text-right">
                          {formatCurrency(purchaseOrder.actualPrice ?? purchaseOrder.price)}
                        </td>
                        <td className="px-3 py-3">
                          <PurchaseOrderStatusPill status={purchaseOrder.status} />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        className="px-3 py-10 text-center text-[0.86rem] text-[var(--theme-text-muted)]"
                        colSpan={7}
                      >
                        No covered purchase orders found for this saved balancing.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function CoveredPurchaseOrderMetric({ label, value }) {
  return (
    <div className="rounded-[16px] border border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] px-4 py-3">
      <span className="block text-[0.7rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
        {label}
      </span>
      <strong className="mt-1 block text-[1rem] text-[var(--theme-text-strong)]">{value}</strong>
    </div>
  );
}

function PurchaseOrderStatusPill({ status }) {
  const normalizedStatus = status || '-';

  return (
    <span className="inline-flex min-h-[26px] items-center rounded-full border border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] px-2.5 text-[0.7rem] font-semibold uppercase tracking-[0.05em] text-[var(--theme-text-subtle)]">
      {normalizedStatus}
    </span>
  );
}

function SupplierBalancingPdfPreviewModal({ record, purchaseOrders = [], transactedBy, onClose }) {
  function handleSavePdf() {
    downloadSupplierBalancingPdf(record, transactedBy, purchaseOrders);
  }

  return (
    <ModalBackdrop backdropClassName="bg-[rgba(33,27,22,0.26)] backdrop-blur-[3px]">
      <div className="w-full max-w-4xl overflow-hidden rounded-[24px] border border-[var(--theme-border-soft)] bg-white shadow-[0_28px_80px_rgba(var(--theme-shadow-rgb),0.24)]">
        <div className="border-b border-[var(--theme-border-soft)] bg-[var(--theme-surface-muted)] px-5 py-5 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[var(--theme-accent)] text-white shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.24)]">
                <Download className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h3 className="m-0 text-[1.2rem] font-semibold text-[var(--theme-text)]">
                  Supplier Balancing PDF Preview
                </h3>
                <p className="m-0 mt-1 text-[0.9rem] leading-5 text-[var(--theme-text-muted)]">
                  {formatSupplierDisplay(record.sacksMark, record.supplierName)} / {formatDate(record.createdAt)}
                </p>
              </div>
            </div>
            <button
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-[#e1d5ca] bg-white text-[var(--theme-text-subtle)] shadow-[0_8px_20px_rgba(var(--theme-shadow-rgb),0.08)] transition hover:bg-[var(--theme-surface-soft)]"
              type="button"
              onClick={onClose}
              aria-label="Close PDF preview"
            >
              x
            </button>
          </div>
        </div>

        <div className="max-h-[70vh] overflow-y-auto bg-[#f6f8f8] p-4 sm:p-6">
          <div className="mx-auto max-w-3xl rounded-[16px] border border-[var(--theme-border-soft)] bg-white p-5 text-[0.9rem] shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.08)]">
            <SupplierBalancingPrintableContent
              record={record}
              purchaseOrders={purchaseOrders}
              transactedBy={transactedBy}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2 border-t border-[var(--theme-border-soft)] bg-white px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
          <button
            className="min-h-[42px] rounded-full border border-[var(--theme-border)] bg-white px-5 text-[0.9rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-full bg-[var(--theme-accent)] px-5 text-[0.9rem] font-semibold text-white shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.22)] transition hover:bg-[var(--theme-accent-strong)]"
            type="button"
            onClick={handleSavePdf}
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Save PDF
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function SupplierBalancingPrintableContent({ record, purchaseOrders = [], transactedBy }) {
  const totals = record.totals || {};
  const inputs = record.inputs || {};
  const includedOrderRows = getSupplierBalancingIncludedOrderRows(
    purchaseOrders,
    totals,
    inputs.balancingAllocations
  );

  return (
    <div className="grid gap-5 text-[var(--theme-text)]">
      <div className="border-b border-[var(--theme-border-soft)] pb-4">
        <p className="m-0 text-[0.72rem] font-semibold uppercase tracking-[0.12em] text-[var(--theme-text-muted)]">
          Supplier Balancing Statement
        </p>
        <h4 className="m-0 mt-1 text-[1.35rem] font-semibold text-[var(--theme-text-strong)]">
          {formatSupplierDisplay(record.sacksMark, record.supplierName)}
        </h4>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <PreviewMeta label="Created" value={formatDate(record.createdAt)} />
          <PreviewMeta label="Transacted By" value={transactedBy || '-'} />
          <PreviewMeta label="Status" value={record.status || 'For Payment'} />
          <PreviewMeta label="Date Range" value={formatBalancingDateRange(record.dateFrom, record.dateTo)} />
        </div>
      </div>

      <PreviewSection title="Included P.O.">
        <PreviewGrid>
          <PreviewMeta label="Included Orders" value={formatNumber(record.purchaseOrderIds?.length || 0, 0)} />
          <PreviewMeta label="Total Sacks" value={formatNumber(totals.totalSacks, 0)} />
          <PreviewMeta label="Price Basis" value={includedOrderRows.length ? 'Per P.O.' : formatCurrency(inputs.pricePerKilo)} />
          <PreviewMeta label="Gross Kilo" value={`${formatNumber(totals.grossKilo)} kg`} />
        </PreviewGrid>
      </PreviewSection>

      <PreviewSection title="Included P.O. Details">
        <PreviewIncludedOrdersTable rows={includedOrderRows} />
      </PreviewSection>

      <PreviewSection title="Weights">
        <PreviewGrid>
          <PreviewMeta label="Net Weight" value={`${formatNumber(totals.netKilo)} kg`} />
          <PreviewMeta label="Dry Weight" value={`${formatNumber(totals.dryKilo)} kg`} />
          <PreviewMeta label="Wet Adjusted" value={`${formatNumber(totals.wetAdjustedKilo)} kg`} />
          <PreviewMeta label="Reject Adjusted" value={`${formatNumber(totals.rejectAdjustedKilo)} kg`} />
        </PreviewGrid>
      </PreviewSection>

      <PreviewSection title="Payment Summary">
        <div className="grid gap-2">
          <PreviewLine label="Dry Pay" value={formatCurrency(totals.dryPay)} />
          <PreviewLine label="Wet Pay" value={formatCurrency(totals.wetPay)} />
          <PreviewLine label="Reject Pay" value={formatCurrency(totals.rejectPay)} />
          <PreviewLine label="Gross Payable" value={formatCurrency(totals.grossPayable)} strong />
          <PreviewLine label="Giveaways" value={formatCurrency(totals.giveawaysTotal)} />
          <PreviewLine label="Karot" value={formatCurrency(totals.karotTotal)} />
          <PreviewLine label="Expense AM" value={formatCurrency(totals.expenseAmTotal)} />
          <PreviewLine label="Expense ZC" value={formatCurrency(totals.expenseZcTotal)} />
          <PreviewLine label="Supplier Savings" value={formatCurrency(totals.savingsTotal)} />
          <PreviewLine label="Cash Advance" value={formatCurrency(totals.cashAdvanceDeductionTotal)} />
          {shouldShowRemainingCashAdvance(totals) ? (
            <PreviewLine
              label="Remaining C.A."
              value={formatCurrency(totals.cashAdvanceRemainingAfterBalancing)}
            />
          ) : null}
          <PreviewLine label="Total Deductions" value={formatCurrency(totals.totalDeductions)} strong />
          <PreviewLine label="Total Balance" value={formatCurrency(totals.totalBalance)} strong large />
        </div>
      </PreviewSection>
    </div>
  );
}

function PreviewSection({ title, children }) {
  return (
    <section className="grid gap-3">
      <h5 className="m-0 text-[0.8rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
        {title}
      </h5>
      {children}
    </section>
  );
}

function PreviewGrid({ children }) {
  return <div className="grid gap-2 sm:grid-cols-2">{children}</div>;
}

function PreviewMeta({ label, value }) {
  return (
    <div className="rounded-[12px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-muted)] px-3 py-2">
      <span className="block text-[0.68rem] font-semibold uppercase tracking-[0.06em] text-[var(--theme-text-muted)]">
        {label}
      </span>
      <strong className="mt-1 block text-[0.9rem] text-[var(--theme-text-strong)]">
        {value}
      </strong>
    </div>
  );
}

function PreviewLine({ label, value, strong = false, large = false }) {
  return (
    <div className={`flex items-center justify-between gap-4 border-b border-[var(--theme-border-soft)] py-2 last:border-b-0 ${strong ? 'font-semibold' : ''}`}>
      <span className="text-[var(--theme-text-muted)]">{label}</span>
      <span className={`${large ? 'text-[1.2rem]' : ''} text-[var(--theme-text-strong)]`}>
        {value}
      </span>
    </div>
  );
}

function PreviewIncludedOrdersTable({ rows = [] }) {
  return (
    <div className="overflow-hidden rounded-[12px] border border-[var(--theme-border-soft)]">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[620px] border-collapse text-[0.78rem]">
          <colgroup>
            <col className="w-[15%]" />
            <col className="w-[42%]" />
            <col className="w-[12%]" />
            <col className="w-[13%]" />
            <col className="w-[18%]" />
          </colgroup>
          <thead>
            <tr className="bg-[var(--theme-accent-panel)] text-left text-[0.64rem] font-semibold uppercase tracking-[0.06em] text-[var(--theme-accent-panel-text)]">
              <th className="px-2.5 py-2">P.O. Date</th>
              <th className="px-2.5 py-2">Lansa Name</th>
              <th className="px-2.5 py-2">Type</th>
              <th className="px-2.5 py-2 text-right">Sacks</th>
              <th className="px-2.5 py-2 text-right">KG Net</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((row, index) => (
                <tr
                  key={`${row.date}-${row.lansaName}-${row.type}-${index}`}
                  className="border-t border-[var(--theme-border-soft)]"
                >
                  <td className="px-2.5 py-2">{row.date}</td>
                  <td className="px-2.5 py-2">{row.lansaName}</td>
                  <td className="px-2.5 py-2">{row.type}</td>
                  <td className="px-2.5 py-2 text-right">{formatIncludedOrderSacks(row)}</td>
                  <td className="px-2.5 py-2 text-right">{formatNumber(row.netKg)} kg</td>
                </tr>
              ))
            ) : (
              <tr className="border-t border-[var(--theme-border-soft)]">
                <td className="px-2.5 py-5 text-center text-[var(--theme-text-muted)]" colSpan={5}>
                  No included P.O. details found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NumberInput({
  label,
  name,
  value,
  onChange,
  formatWithCommas = false,
  prefix = '',
  disabled = false,
  displayOnly = false,
}) {
  const displayValue = formatWithCommas ? formatInputWithCommas(value) : value;

  return (
    <label className="grid w-full min-w-0 gap-1.5">
      <span className={`text-[0.74rem] font-medium ${disabled ? 'text-[var(--theme-text-muted)]' : 'text-[var(--theme-text-subtle)]'}`}>
        {label}
      </span>
      <span
        className={`flex min-h-[40px] items-center rounded-full border px-3 transition ${
          displayOnly
            ? 'border-[var(--theme-border)] bg-[var(--theme-surface-muted)] text-[var(--theme-text-strong)]'
            : disabled
            ? 'border-[#d8dfe0] bg-[#eef1f1] text-[var(--theme-text-muted)] shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]'
            : 'border-[var(--theme-border)] bg-white focus-within:border-[var(--theme-accent)] focus-within:ring-2 focus-within:ring-[var(--theme-accent-soft)]'
        }`}
        aria-readonly={displayOnly}
      >
        {prefix ? (
          <span className="mr-2 shrink-0 text-[0.84rem] text-[var(--theme-text-muted)]">
            {prefix}
          </span>
        ) : null}
        {displayOnly ? (
          <>
            <span className="min-w-0 flex-1 truncate text-[0.84rem] font-semibold">
              {displayValue || '0'}
            </span>
            <Lock
              className="ml-2 h-3.5 w-3.5 shrink-0 text-[var(--theme-text-muted)]"
              aria-label="Display only"
            />
          </>
        ) : (
          <input
            className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[0.84rem] outline-none"
            aria-disabled={disabled}
            type="text"
            inputMode="decimal"
            name={name}
            value={displayValue}
            onChange={onChange}
            placeholder="0"
            disabled={disabled}
          />
        )}
      </span>
    </label>
  );
}

function MoneyInput(props) {
  return <NumberInput {...props} prefix="₱" />;
}

function MobileDetail({ label, value }) {
  return (
    <div className="w-full min-w-0 rounded-[10px] bg-white/70 px-2.5 py-2">
      <span className="block text-[0.68rem] font-semibold uppercase tracking-[0.06em] text-[var(--theme-text-muted)]">
        {label}
      </span>
      <span className="mt-1 block truncate font-semibold text-[var(--theme-text)]">{value}</span>
    </div>
  );
}

function ReadOnlyMetric({ label, value }) {
  return (
    <div className="grid min-h-[58px] w-full min-w-0 gap-1 rounded-[14px] border border-[var(--theme-border)] bg-[var(--theme-surface-muted)] px-3 py-2">
      <span className="text-[0.74rem] font-medium text-[var(--theme-text-subtle)]">{label}</span>
      <strong className="truncate text-[0.9rem] font-semibold text-[var(--theme-text-strong)]">
        {value}
      </strong>
    </div>
  );
}

function formatInputWithCommas(value) {
  const normalizedValue = String(value ?? '').trim();

  if (!normalizedValue) {
    return '';
  }

  const [integerPart, decimalPart] = normalizedValue.replace(/,/g, '').split('.');
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return typeof decimalPart === 'undefined'
    ? formattedInteger
    : `${formattedInteger}.${decimalPart}`;
}

function formatFixedDecimalInputValue(value, decimalPlaces) {
  const normalizedValue = String(value ?? '').replace(/,/g, '').trim();

  if (!normalizedValue) {
    return '';
  }

  const numericValue = Number(normalizedValue);

  if (!Number.isFinite(numericValue)) {
    return String(value ?? '');
  }

  return numericValue.toFixed(decimalPlaces);
}

function SummaryRow({ label, value, strong = false, tone = 'neutral' }) {
  const rowSpacingClassName = tone === 'neutral' ? 'border-b pb-2 last:pb-0' : 'my-1';
  const toneClassName =
    tone === 'success'
      ? 'rounded-[10px] border border-[color-mix(in_srgb,var(--theme-accent-panel-text)_34%,transparent)] bg-[color-mix(in_srgb,var(--theme-accent-panel-text)_10%,transparent)] px-2 py-1.5'
      : tone === 'warning'
        ? 'rounded-[10px] border border-[color-mix(in_srgb,var(--theme-accent-panel-text)_28%,transparent)] bg-[color-mix(in_srgb,var(--theme-accent-panel-text)_8%,transparent)] px-2 py-1.5'
        : '';
  const valueToneClassName =
    tone === 'success'
      ? 'text-[var(--theme-accent-panel-text)]'
      : tone === 'warning'
        ? 'text-[var(--theme-accent-panel-text)]'
        : 'text-[var(--theme-accent-panel-text)]';

  return (
    <div className={`flex min-w-0 items-center justify-between gap-3 border-white/45 text-[0.84rem] ${rowSpacingClassName} ${toneClassName}`}>
      <span className="min-w-0 text-[var(--theme-accent-panel-text)]">{label}</span>
      <span className={`shrink-0 text-right ${strong ? 'font-semibold' : 'font-medium'} ${valueToneClassName}`}>
        {value}
      </span>
    </div>
  );
}

function HighlightedTotal({ children }) {
  return (
    <span className="inline-flex min-h-[28px] items-center rounded-full bg-[#edf8ef] px-3 text-[0.82rem] font-semibold text-[#3f7545] ring-1 ring-[#cfe4d1]">
      {children}
    </span>
  );
}

function SplitAllocationBadge() {
  return (
    <span
      className="relative inline-flex h-5 items-center rounded-full border border-[color-mix(in_srgb,var(--theme-accent)_42%,transparent)] bg-[var(--theme-accent-soft)] px-2 text-[0.62rem] font-bold uppercase tracking-[0.06em] text-[var(--theme-accent-strong)]"
      title="This P.O. has split price allocations"
    >
      <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-[var(--theme-accent)] opacity-75 animate-ping" />
      Split
    </span>
  );
}

function BalancingAllocationEditor({
  purchaseOrder,
  selectedPurchaseOrders,
  lines,
  onAddLine,
  onChangeLine,
  onRemoveLine,
  onReset,
}) {
  const purchaseOrderSacks = getPurchaseOrderSacks(purchaseOrder);
  const allocatedSacks = lines.reduce((total, line) => total + toNumber(line.sacks), 0);
  const remainingSacks = roundNumber(purchaseOrderSacks - allocatedSacks);
  const customPriceInputRefs = useRef({});
  const previousPriceBasisByLineIdRef = useRef({});
  const priceOptions = selectedPurchaseOrders.map((selectedPurchaseOrder) => ({
    value: String(selectedPurchaseOrder.id),
    label: `${formatPurchaseOrderCode(selectedPurchaseOrder)} - ${formatCurrency(getPurchaseOrderPrice(selectedPurchaseOrder))}`,
  })).concat({
    value: CUSTOM_PRICE_BASIS_VALUE,
    label: 'Custom price',
  });

  useEffect(() => {
    let nextFocusLineId = '';
    const nextPriceBasisByLineId = {};

    lines.forEach((line) => {
      const lineId = String(line.id);
      const priceBasis = String(line.priceSourcePurchaseOrderId);
      const previousPriceBasis = previousPriceBasisByLineIdRef.current[lineId];

      nextPriceBasisByLineId[lineId] = priceBasis;

      if (
        priceBasis === CUSTOM_PRICE_BASIS_VALUE &&
        previousPriceBasis &&
        previousPriceBasis !== CUSTOM_PRICE_BASIS_VALUE
      ) {
        nextFocusLineId = lineId;
      }
    });

    previousPriceBasisByLineIdRef.current = nextPriceBasisByLineId;

    if (!nextFocusLineId) {
      return undefined;
    }

    const focusTimer = window.setTimeout(() => {
      const input = customPriceInputRefs.current[nextFocusLineId];

      if (input) {
        input.focus();
        input.select();
      }
    }, 0);

    return () => window.clearTimeout(focusTimer);
  }, [lines]);

  return (
    <div className="grid gap-3 rounded-[14px] border border-[var(--theme-border-soft)] bg-white p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="m-0 text-[0.82rem] font-semibold text-[var(--theme-text-strong)]">
            Balancing split for {formatPurchaseOrderCode(purchaseOrder)}
          </p>
          <p className="m-0 mt-1 text-[0.74rem] text-[var(--theme-text-muted)]">
            KG Net is split by sacks. Original P.O. price stays unchanged.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`rounded-full px-3 py-1 text-[0.74rem] font-semibold ${
              Math.abs(remainingSacks) <= 0.009
                ? 'bg-[#edf8ef] text-[#3f7545] ring-1 ring-[#cfe4d1]'
                : 'bg-[#fff8e6] text-[#8a6424] ring-1 ring-[#ead7a8]'
            }`}
          >
            Allocated {formatNumber(allocatedSacks, 2)} / {formatNumber(purchaseOrderSacks, 0)}
          </span>
          <button
            className="inline-flex min-h-[30px] items-center gap-1.5 rounded-full border border-[var(--theme-border)] bg-white px-3 text-[0.74rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onReset}
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            Reset
          </button>
        </div>
      </div>

      <div className="grid gap-2">
        {lines.map((line, index) => {
          const sourceNetKg = getPurchaseOrderNetKilo(purchaseOrder);
          const allocatedNetKg =
            purchaseOrderSacks > 0
              ? roundNumber(sourceNetKg * (toNumber(line.sacks) / purchaseOrderSacks))
              : 0;
          const isCustomPrice = String(line.priceSourcePurchaseOrderId) === CUSTOM_PRICE_BASIS_VALUE;
          const priceSource = isCustomPrice
            ? null
            : selectedPurchaseOrders.find(
                (selectedPurchaseOrder) =>
                  String(selectedPurchaseOrder.id) === String(line.priceSourcePurchaseOrderId)
              ) || purchaseOrder;
          const displayedPrice = isCustomPrice
            ? line.customPricePerKilo
            : getPurchaseOrderPrice(priceSource);

          return (
            <div
              key={line.id}
              className="grid gap-3 rounded-[12px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-muted)] p-3 xl:grid-cols-[96px_minmax(260px,1fr)_136px_120px_40px] xl:items-start"
            >
              <label className="grid min-w-0 gap-1 text-[0.72rem] font-semibold uppercase tracking-[0.05em] text-[var(--theme-text-muted)]">
                Sacks
                <input
                  className="h-11 w-full rounded-[10px] border border-[var(--theme-border)] bg-white px-3 text-right text-[0.9rem] font-semibold text-[var(--theme-text)] outline-none transition focus:border-[var(--theme-accent)] focus:ring-2 focus:ring-[var(--theme-focus)]"
                  value={line.sacks}
                  onChange={(event) => onChangeLine(line.id, 'sacks', event.target.value)}
                  inputMode="decimal"
                />
              </label>
              <CustomSelect
                label="Price Basis"
                name={`priceSourcePurchaseOrderId-${line.id}`}
                value={String(line.priceSourcePurchaseOrderId)}
                onChange={(event) =>
                  onChangeLine(line.id, 'priceSourcePurchaseOrderId', event.target.value)
                }
                options={priceOptions.map((option) => ({
                  ...option,
                  label:
                    option.value === String(purchaseOrder.id) && index === 0
                      ? `Own price - ${formatCurrency(getPurchaseOrderPrice(purchaseOrder))}`
                      : option.label,
                }))}
                searchable={false}
                wrapperClassName="relative grid w-full min-w-0 gap-1"
                labelClassName="text-[0.72rem] font-semibold uppercase tracking-[0.05em] text-[var(--theme-text-muted)]"
                triggerShapeClassName="rounded-[10px]"
                triggerClassName="min-h-[44px] text-[0.86rem] font-semibold"
              />
              <ReadOnlyAllocationValue label="KG Net" value={`${formatNumber(allocatedNetKg)} kg`} />
              {isCustomPrice ? (
                <label className="grid min-w-0 gap-1 text-[0.72rem] font-semibold uppercase tracking-[0.05em] text-[#8a4f00]">
                  Price
                  <input
                    ref={(input) => {
                      customPriceInputRefs.current[String(line.id)] = input;
                    }}
                    className="h-11 w-full rounded-[10px] border-2 border-[#f59e0b] bg-[#fff3bf] px-3 text-right text-[0.9rem] font-bold text-[#4a2d00] shadow-[0_0_0_3px_rgba(245,158,11,0.18)] outline-none transition placeholder:text-[#a66a00] focus:border-[#d97706] focus:bg-[#ffe08a] focus:ring-2 focus:ring-[#f59e0b]/45"
                    value={line.customPricePerKilo}
                    onChange={(event) => onChangeLine(line.id, 'customPricePerKilo', event.target.value)}
                    inputMode="decimal"
                    placeholder="0.00"
                  />
                </label>
              ) : (
                <ReadOnlyAllocationValue label="Price" value={formatCurrency(displayedPrice)} />
              )}
              <button
                className="mt-[1.35rem] inline-flex h-11 w-11 items-center justify-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-40 xl:mt-[1.35rem]"
                type="button"
                onClick={() => onRemoveLine(line.id)}
                disabled={lines.length <= 1}
                aria-label="Remove split line"
                title="Remove split line"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          );
        })}
      </div>

      {Math.abs(remainingSacks) > 0.009 ? (
        <p className="m-0 rounded-[10px] border border-[#ead7a8] bg-[#fff8e6] px-3 py-2 text-[0.78rem] font-semibold text-[#8a6424]">
          Allocated sacks must equal {formatNumber(purchaseOrderSacks, 0)}. Remaining: {formatNumber(remainingSacks, 2)} sacks.
        </p>
      ) : null}

      <button
        className="inline-flex min-h-[34px] w-fit items-center gap-1.5 rounded-full border border-[var(--theme-border)] bg-white px-3 text-[0.78rem] font-semibold text-[var(--theme-accent)] transition hover:bg-[var(--theme-surface-soft)]"
        type="button"
        onClick={onAddLine}
      >
        <Plus className="h-3.5 w-3.5" aria-hidden="true" />
        Add price line
      </button>
    </div>
  );
}

function ReadOnlyAllocationValue({ label, value }) {
  return (
    <div className="grid min-w-0 gap-1 text-[0.72rem] font-semibold uppercase tracking-[0.05em] text-[var(--theme-text-muted)]">
      {label}
      <span className="flex h-11 min-w-0 items-center justify-end truncate rounded-[10px] border border-[var(--theme-border-soft)] bg-white px-3 text-[0.86rem] font-semibold normal-case tracking-normal text-[var(--theme-text)]">
        {value}
      </span>
    </div>
  );
}

function isBalancingLockedByCompletedPurchaseOrder(record, purchaseOrders = []) {
  const includedPurchaseOrderIds = new Set(
    (record?.purchaseOrderIds || []).map((purchaseOrderId) => String(purchaseOrderId))
  );

  if (!includedPurchaseOrderIds.size) {
    return false;
  }

  return purchaseOrders.some(
    (purchaseOrder) =>
      includedPurchaseOrderIds.has(String(purchaseOrder.id)) &&
      purchaseOrder.status === 'Completed'
  );
}

function getCoveredPurchaseOrders(record, purchaseOrders = []) {
  const purchaseOrdersById = new Map(
    purchaseOrders.map((purchaseOrder) => [String(purchaseOrder.id), purchaseOrder])
  );

  return (record?.purchaseOrderIds || [])
    .map((purchaseOrderId) => purchaseOrdersById.get(String(purchaseOrderId)))
    .filter(Boolean);
}

function getSupplierBalancingIncludedOrderRows(purchaseOrders = [], totals = {}, allocations = []) {
  const targetNetKilo = toNumber(totals.netKilo);
  const allocationLines = Array.isArray(allocations) && allocations.length
    ? allocations
    : buildBalancingAllocationLines(purchaseOrders, {});
  const rows = purchaseOrders.flatMap((purchaseOrder) => {
    const purchaseOrderAllocationLines = allocationLines.filter(
      (line) => String(line.sourcePurchaseOrderId) === String(purchaseOrder.id)
    );
    const receivingItems = Array.isArray(purchaseOrder.receivingItems)
      ? purchaseOrder.receivingItems
      : [];
    const dryWetItems = receivingItems.filter((item) =>
      ['Dry', 'Wet', 'Reject'].includes(String(item.category || '').trim())
    );
    const baseRow = {
      date: formatDate(purchaseOrder.poDate),
      price: getPurchaseOrderPrice(purchaseOrder),
      lansaName: getPurchaseOrderLansaName(purchaseOrder),
    };

    if (!dryWetItems.length) {
      const baseRows = purchaseOrderAllocationLines.length
        ? purchaseOrderAllocationLines
        : [createComputedBalancingAllocationLine(purchaseOrder)];

      return baseRows.map((line) => [
        {
          ...baseRow,
          price: toNumber(line.pricePerKilo) || baseRow.price,
          netKgBasis: 0,
          sacks: toNumber(line.sacks),
          type: 'Dry',
        },
      ]).flat();
    }

    const sourceSacks = getPurchaseOrderSacks(purchaseOrder);
    const baseRows = purchaseOrderAllocationLines.length
      ? purchaseOrderAllocationLines
      : [createComputedBalancingAllocationLine(purchaseOrder)];

    return baseRows.flatMap((line) => {
      const lineShare = sourceSacks > 0 ? toNumber(line.sacks) / sourceSacks : 0;

      return dryWetItems.map((item) => ({
        ...baseRow,
        price: toNumber(line.pricePerKilo) || baseRow.price,
        netKgBasis: getReceivingNetKilo(item) * lineShare,
        sacks: getReceivingSacks(item) * lineShare,
        type: String(item.category || '').trim() || '-',
      }));
    });
  });
  const netKiloBasisTotal = rows.reduce((total, row) => total + toNumber(row.netKgBasis), 0);
  const sacksBasisTotal = rows.reduce((total, row) => total + toNumber(row.sacks), 0);

  const normalizedRows = rows.map((row) => {
    const netKg = netKiloBasisTotal > 0
      ? getScaledKiloShare(row.netKgBasis, netKiloBasisTotal, targetNetKilo)
      : getScaledKiloShare(row.sacks, sacksBasisTotal, targetNetKilo);

    return {
      ...row,
      netKg: roundNumber(netKg),
    };
  });

  if (normalizedRows.length && targetNetKilo > 0) {
    const roundedTotal = normalizedRows.reduce((total, row) => total + toNumber(row.netKg), 0);
    const roundingDifference = roundNumber(targetNetKilo - roundedTotal);

    if (roundingDifference) {
      const lastRow = normalizedRows[normalizedRows.length - 1];
      normalizedRows[normalizedRows.length - 1] = {
        ...lastRow,
        netKg: roundNumber(toNumber(lastRow.netKg) + roundingDifference),
      };
    }
  }

  return normalizedRows;
}

function createInitialBalancingValues() {
  return {
    supplierId: '',
    dateFrom: '',
    dateTo: '',
    pricePerKilo: '',
    grossKilo: '',
    drySacks: '',
    wetSacks: '',
    rejectSacks: '',
    rejectAdjustmentKilo: '',
    wetAdjustmentPercent: '',
    giveaways: '',
    karotPerSack: '',
    expenseAmPerSack: '',
    expenseZcPerSack: '',
    tareMultiplier: '',
    savingsEnabled: false,
    savingsPerKilo: '',
    tarePayment: '',
  };
}

function createBalancingValuesFromRecord(record) {
  const inputs = record?.inputs || {};

  return {
    supplierId: toInputString(record?.supplierId),
    dateFrom: record?.dateFrom || '',
    dateTo: record?.dateTo || '',
    pricePerKilo: toInputString(inputs.pricePerKilo),
    grossKilo: '',
    drySacks: '',
    wetSacks: '',
    rejectSacks: '',
    rejectAdjustmentKilo: toInputString(inputs.rejectAdjustmentKilo),
    wetAdjustmentPercent: toInputString(inputs.wetAdjustmentPercent),
    giveaways: toInputString(inputs.giveaways),
    karotPerSack: toInputString(inputs.karotPerSack),
    expenseAmPerSack: toInputString(inputs.expenseAmPerSack),
    expenseZcPerSack: toInputString(inputs.expenseZcPerSack),
    tareMultiplier: toInputString(inputs.tareMultiplier),
    savingsEnabled: Boolean(toNumber(inputs.savingsEnabled)),
    savingsPerKilo: toInputString(inputs.savingsPerKilo),
    tarePayment: toInputString(inputs.tarePayment),
  };
}

function createManualAutoFieldsFromRecord(record) {
  const inputs = record?.inputs || {};

  return Array.from(AUTO_FILL_FIELD_NAMES).reduce((fields, fieldName) => {
    if (
      Object.prototype.hasOwnProperty.call(inputs, fieldName) &&
      (!PURCHASE_ORDER_SELECTION_AUTO_FIELD_NAMES.has(fieldName) || fieldName === 'giveaways')
    ) {
      fields[fieldName] = true;
    }

    return fields;
  }, {});
}

function createBalancingAllocationsFromRecord(record) {
  const allocations = Array.isArray(record?.inputs?.balancingAllocations)
    ? record.inputs.balancingAllocations
    : [];

  return allocations.reduce((groups, allocation, index) => {
    const sourcePurchaseOrderId = String(allocation.sourcePurchaseOrderId || '');

    if (!sourcePurchaseOrderId) {
      return groups;
    }

    if (!groups[sourcePurchaseOrderId]) {
      groups[sourcePurchaseOrderId] = [];
    }

    groups[sourcePurchaseOrderId].push(
      normalizeBalancingAllocationLine({
        id: allocation.id || `${sourcePurchaseOrderId}-${index}`,
        sacks: toInputString(allocation.sacks),
        priceSourcePurchaseOrderId:
          allocation.isCustomPrice || allocation.priceSourcePurchaseOrderId === CUSTOM_PRICE_BASIS_VALUE
            ? CUSTOM_PRICE_BASIS_VALUE
            : allocation.priceSourcePurchaseOrderId || sourcePurchaseOrderId,
        customPricePerKilo: toInputString(
          allocation.customPricePerKilo ?? allocation.pricePerKilo
        ),
      })
    );

    return groups;
  }, {});
}

function createDefaultBalancingAllocation(purchaseOrder, overrides = {}) {
  return normalizeBalancingAllocationLine({
    id: `${purchaseOrder.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    sacks: toInputString(overrides.sacks ?? getPurchaseOrderSacks(purchaseOrder)),
    priceSourcePurchaseOrderId: overrides.priceSourcePurchaseOrderId || String(purchaseOrder.id),
    customPricePerKilo: toInputString(overrides.customPricePerKilo ?? getPurchaseOrderPrice(purchaseOrder)),
  });
}

function normalizeBalancingAllocationLine(line) {
  return {
    id: String(line.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
    sacks: String(line.sacks ?? ''),
    priceSourcePurchaseOrderId: String(line.priceSourcePurchaseOrderId || ''),
    customPricePerKilo: String(line.customPricePerKilo ?? ''),
  };
}

function getNextBalancingAllocationLine(
  line,
  fieldName,
  value,
  purchaseOrder,
  selectedPurchaseOrders = []
) {
  if (fieldName === 'sacks') {
    return {
      ...line,
      sacks: sanitizeAllocationSacksValue(value, getPurchaseOrderSacks(purchaseOrder)),
    };
  }

  if (fieldName === 'priceSourcePurchaseOrderId') {
    const isCustomPrice = String(value) === CUSTOM_PRICE_BASIS_VALUE;
    const wasCustomPrice =
      String(line.priceSourcePurchaseOrderId) === CUSTOM_PRICE_BASIS_VALUE;

    return {
      ...line,
      priceSourcePurchaseOrderId: value,
      customPricePerKilo: isCustomPrice
        ? wasCustomPrice
          ? line.customPricePerKilo
          : toInputString(getAllocationLineDisplayPrice(line, purchaseOrder, selectedPurchaseOrders))
        : line.customPricePerKilo,
    };
  }

  return {
    ...line,
    [fieldName]: value,
  };
}

function getAllocationLineDisplayPrice(line, purchaseOrder, selectedPurchaseOrders = []) {
  if (String(line.priceSourcePurchaseOrderId) === CUSTOM_PRICE_BASIS_VALUE) {
    return toNumber(line.customPricePerKilo);
  }

  const priceSourcePurchaseOrder = selectedPurchaseOrders.find(
    (selectedPurchaseOrder) =>
      String(selectedPurchaseOrder.id) === String(line.priceSourcePurchaseOrderId)
  ) || purchaseOrder;

  return getPurchaseOrderPrice(priceSourcePurchaseOrder);
}

function buildBalancingAllocationLines(purchaseOrders = [], allocationGroups = {}) {
  const purchaseOrdersById = new Map(
    purchaseOrders.map((purchaseOrder) => [String(purchaseOrder.id), purchaseOrder])
  );

  return purchaseOrders.flatMap((purchaseOrder) => {
    const purchaseOrderId = String(purchaseOrder.id);
    const lines = allocationGroups[purchaseOrderId];
    const sourceLines = Array.isArray(lines) && lines.length
      ? lines
      : [createComputedBalancingAllocationLine(purchaseOrder)];

    return sourceLines.map((line) => {
      const isCustomPrice = String(line.priceSourcePurchaseOrderId) === CUSTOM_PRICE_BASIS_VALUE;
      const priceSourcePurchaseOrder = isCustomPrice
        ? null
        : purchaseOrdersById.get(String(line.priceSourcePurchaseOrderId)) || purchaseOrder;

      return {
        id: line.id,
        sourcePurchaseOrderId: purchaseOrder.id,
        priceSourcePurchaseOrderId: isCustomPrice
          ? CUSTOM_PRICE_BASIS_VALUE
          : priceSourcePurchaseOrder.id,
        isCustomPrice,
        sacks: toNumber(line.sacks),
        pricePerKilo: isCustomPrice
          ? toNumber(line.customPricePerKilo)
          : getPurchaseOrderPrice(priceSourcePurchaseOrder),
        sourceMoistureContent: getPurchaseOrderMoistureContent(purchaseOrder),
      };
    });
  });
}

function hasCustomBalancingAllocation(purchaseOrder, lines = []) {
  if (!Array.isArray(lines) || !lines.length) {
    return false;
  }

  const purchaseOrderId = String(purchaseOrder.id);

  if (lines.length > 1) {
    return true;
  }

  const [line] = lines;
  const lineSacks = toNumber(line.sacks);
  const purchaseOrderSacks = getPurchaseOrderSacks(purchaseOrder);

  return (
    String(line.priceSourcePurchaseOrderId) !== purchaseOrderId ||
    String(line.priceSourcePurchaseOrderId) === CUSTOM_PRICE_BASIS_VALUE ||
    Math.abs(lineSacks - purchaseOrderSacks) > 0.009
  );
}

function createComputedBalancingAllocationLine(purchaseOrder) {
  return {
    id: `${purchaseOrder.id}-own`,
    sourcePurchaseOrderId: purchaseOrder.id,
    priceSourcePurchaseOrderId: purchaseOrder.id,
    sacks: getPurchaseOrderSacks(purchaseOrder),
    pricePerKilo: getPurchaseOrderPrice(purchaseOrder),
    sourceMoistureContent: getPurchaseOrderMoistureContent(purchaseOrder),
  };
}

function validateBalancingAllocations(purchaseOrders = [], allocationGroups = {}) {
  return purchaseOrders.flatMap((purchaseOrder) => {
    const purchaseOrderId = String(purchaseOrder.id);
    const lines = allocationGroups[purchaseOrderId];

    if (!Array.isArray(lines) || !lines.length) {
      return [];
    }

    const purchaseOrderSacks = getPurchaseOrderSacks(purchaseOrder);
    const allocatedSacks = lines.reduce((total, line) => total + toNumber(line.sacks), 0);
    const errors = [];

    if (lines.some((line) => toNumber(line.sacks) <= 0)) {
      errors.push(`${formatPurchaseOrderCode(purchaseOrder)} has a split line with zero sacks.`);
    }

    if (
      lines.some(
        (line) =>
          String(line.priceSourcePurchaseOrderId) === CUSTOM_PRICE_BASIS_VALUE &&
          toNumber(line.customPricePerKilo) <= 0
      )
    ) {
      errors.push(`${formatPurchaseOrderCode(purchaseOrder)} has a custom price that must be greater than zero.`);
    }

    if (Math.abs(purchaseOrderSacks - allocatedSacks) > 0.009) {
      errors.push(
        `${formatPurchaseOrderCode(purchaseOrder)} split sacks must equal ${formatNumber(purchaseOrderSacks, 0)}.`
      );
    }

    return errors;
  });
}

function serializeBalancingAllocations(lines = []) {
  const linesByPurchaseOrderId = lines.reduce((groups, line) => {
    const purchaseOrderId = String(line.sourcePurchaseOrderId || '');

    if (!purchaseOrderId) {
      return groups;
    }

    if (!groups.has(purchaseOrderId)) {
      groups.set(purchaseOrderId, []);
    }

    groups.get(purchaseOrderId).push(line);
    return groups;
  }, new Map());

  return Array.from(linesByPurchaseOrderId.values()).flatMap((groupedLines) => {
    const hasCustomLines =
      groupedLines.length > 1 ||
      groupedLines.some(
        (line) =>
          String(line.sourcePurchaseOrderId) !== String(line.priceSourcePurchaseOrderId) ||
          line.isCustomPrice
      );

    if (!hasCustomLines) {
      return [];
    }

    return groupedLines.map((line) => ({
      id: line.id,
      sourcePurchaseOrderId: line.sourcePurchaseOrderId,
      priceSourcePurchaseOrderId: line.isCustomPrice
        ? CUSTOM_PRICE_BASIS_VALUE
        : line.priceSourcePurchaseOrderId,
      isCustomPrice: Boolean(line.isCustomPrice),
      customPricePerKilo: line.isCustomPrice ? roundNumber(line.pricePerKilo) : undefined,
      sacks: roundNumber(line.sacks),
      pricePerKilo: roundNumber(line.pricePerKilo),
    }));
  });
}

function getRemainingAllocationSacks(purchaseOrder, lines = []) {
  return roundNumber(
    getPurchaseOrderSacks(purchaseOrder) -
      lines.reduce((total, line) => total + toNumber(line.sacks), 0)
  );
}

function removeObjectKey(value, key) {
  const nextValue = { ...value };
  delete nextValue[key];
  return nextValue;
}

function toInputString(value) {
  if (value === null || typeof value === 'undefined') {
    return '';
  }

  return String(value);
}

function getFilteredPurchaseOrders(purchaseOrders, values, activeBalancingPurchaseOrderIds = new Set()) {
  if (!values.supplierId) {
    return [];
  }

  return purchaseOrders
    .filter((purchaseOrder) => {
      const matchesSupplier =
        String(purchaseOrder.supplierId) === String(values.supplierId);
      const poDate = String(purchaseOrder.poDate || '').slice(0, 10);
      const matchesDateFrom = !values.dateFrom || poDate >= values.dateFrom;
      const matchesDateTo = !values.dateTo || poDate <= values.dateTo;

      return (
        matchesSupplier &&
        matchesDateFrom &&
        matchesDateTo &&
        isPendingBalancingPurchaseOrder(purchaseOrder, activeBalancingPurchaseOrderIds)
      );
    })
    .sort((firstOrder, secondOrder) =>
      String(firstOrder.poDate || '').localeCompare(String(secondOrder.poDate || ''))
    );
}

function getPendingBalancingPurchaseOrderCounts(purchaseOrders, activeBalancingPurchaseOrderIds) {
  return purchaseOrders.reduce((counts, purchaseOrder) => {
    if (!isPendingBalancingPurchaseOrder(purchaseOrder, activeBalancingPurchaseOrderIds)) {
      return counts;
    }

    const supplierId = String(purchaseOrder.supplierId || '');

    if (!supplierId) {
      return counts;
    }

    counts.set(supplierId, (counts.get(supplierId) || 0) + 1);
    return counts;
  }, new Map());
}

function isPendingBalancingPurchaseOrder(purchaseOrder, activeBalancingPurchaseOrderIds = new Set()) {
  const isSacksOrder = purchaseOrder.stockUnit === 'sacks';
  const isUsableStatus =
    BALANCING_INCLUDED_PO_STATUSES.has(purchaseOrder.status) &&
    (purchaseOrder.status !== 'Received' || Boolean(purchaseOrder.balancingReadyAt));
  const isActiveBalancingPurchaseOrder = activeBalancingPurchaseOrderIds.has(
    String(purchaseOrder.id)
  );

  return isSacksOrder && isUsableStatus && !isActiveBalancingPurchaseOrder;
}

function getActiveBalancingPurchaseOrderIds(records) {
  return new Set(
    records
      .filter((record) => ['For Payment', 'Paid'].includes(record.status))
      .flatMap((record) => record.purchaseOrderIds || [])
      .map((purchaseOrderId) => String(purchaseOrderId))
  );
}

function getSupplierOpenCashAdvanceTotal(records, supplierId) {
  if (!supplierId) {
    return 0;
  }

  return records
    .filter(
      (record) =>
        String(record.supplierId) === String(supplierId) && record.status === 'Open'
    )
    .reduce(
      (total, record) =>
        total + toNumber(record.remainingAmount ?? record.amount),
      0
    );
}

function getSupplierIdsWithSavingsRecords(records = []) {
  return records.reduce((supplierIds, record) => {
    const supplierId = String(record?.supplierId || '');

    if (
      supplierId &&
      String(record?.status || '') === 'Paid' &&
      Boolean(toNumber(record?.inputs?.savingsEnabled))
    ) {
      supplierIds.add(supplierId);
    }

    return supplierIds;
  }, new Set());
}

function getBalancingTotals(
  values,
  purchaseOrders,
  allocationLines,
  computedInputs,
  manualAutoFields,
  tareAmountFee,
  cashAdvanceTotal
) {
  const purchaseOrderSacks = purchaseOrders.reduce(
    (total, purchaseOrder) => total + getPurchaseOrderSacks(purchaseOrder),
    0
  );
  const drySacks = getAutoInputNumber('drySacks', values, computedInputs.drySacks, manualAutoFields);
  const wetSacks = getAutoInputNumber('wetSacks', values, computedInputs.wetSacks, manualAutoFields);
  const rejectSacks = getAutoInputNumber(
    'rejectSacks',
    values,
    computedInputs.rejectSacks,
    manualAutoFields
  );
  const rejectInputKilo = rejectSacks;
  const inputTotalSacks = drySacks + wetSacks;
  const totalSacks = inputTotalSacks > 0 ? inputTotalSacks : purchaseOrderSacks;
  const pricePerKilo = getInputNumber(values.pricePerKilo, computedInputs.pricePerKilo);
  const grossKilo = getAutoInputNumber('grossKilo', values, computedInputs.grossKilo, manualAutoFields);
  const grossNetKilo = Math.max(0, grossKilo - totalSacks * SACK_WEIGHT_KG);
  const rejectKilo = Math.min(rejectInputKilo, grossNetKilo);
  const netKilo = Math.max(0, grossNetKilo - rejectKilo);
  const wetShareKilo = totalSacks > 0 ? (netKilo / totalSacks) * wetSacks : 0;
  const rejectAdjustedKilo = Math.max(0, rejectKilo - toNumber(values.rejectAdjustmentKilo));
  const wetBaseKilo = Math.floor(
    wetShareKilo * (1 + toNumber(values.wetAdjustmentPercent) / 100)
  );
  const wetAdjustmentTotals = getPerPurchaseOrderWetAdjustmentTotals(purchaseOrders, {
    allocationLines,
    wetBaseKilo,
  });
  const wetDeductionKilo = wetAdjustmentTotals.wetDeductionKilo;
  const wetAdjustedKilo = wetAdjustmentTotals.wetAdjustedKilo;
  const dryKilo = Math.max(0, netKilo - wetBaseKilo);
  const paymentTotals = getPerPurchaseOrderPaymentTotals(purchaseOrders, {
    allocationLines,
    dryKilo,
    wetPaymentBases: wetAdjustmentTotals.paymentBases,
    wetAdjustedKilo,
    rejectKilo,
    rejectAdjustedKilo,
    wetAdjustmentPercent: toNumber(values.wetAdjustmentPercent),
    rejectAdjustmentKilo: toNumber(values.rejectAdjustmentKilo),
  });
  const dryPay = paymentTotals.dryHasBasis ? paymentTotals.dryPay : dryKilo * pricePerKilo;
  const wetPay = paymentTotals.wetHasBasis ? paymentTotals.wetPay : wetAdjustedKilo * pricePerKilo;
  const rejectPay = paymentTotals.rejectHasBasis
    ? paymentTotals.rejectPay
    : rejectAdjustedKilo * pricePerKilo;
  const grossPayable = dryPay + wetPay + rejectPay;
  const karotTotal = toNumber(values.karotPerSack) * totalSacks;
  const giveawaysDefault = dryKilo / 2;
  const giveawaysTotal = getAutoInputNumber(
    'giveaways',
    values,
    giveawaysDefault,
    manualAutoFields
  );
  const totalAdditions =
    giveawaysTotal + karotTotal;
  const expenseAmTotal = toNumber(values.expenseAmPerSack) * totalSacks;
  const tarePayment = getAutoInputNumber(
    'tarePayment',
    values,
    tareAmountFee,
    manualAutoFields
  );
  const expenseZcPerSack = toNumber(values.expenseZcPerSack);
  const tareMultiplier = getAutoInputNumber(
    'tareMultiplier',
    values,
    purchaseOrders.length,
    manualAutoFields
  );
  const hasExpenseZcInput = expenseZcPerSack > 0;
  const expenseZcTotal =
    totalSacks > 0 && hasExpenseZcInput
      ? expenseZcPerSack * totalSacks + tareMultiplier * tarePayment
      : 0;
  const savingsPotentialTotal = values.savingsEnabled
    ? dryKilo * toNumber(values.savingsPerKilo)
    : 0;
  const payableBeforeSavingsAndCashAdvance =
    grossPayable + totalAdditions - expenseAmTotal - expenseZcTotal;
  const savingsDeductionLimit = Math.max(0, payableBeforeSavingsAndCashAdvance);
  const savingsTotal = Math.min(savingsPotentialTotal, savingsDeductionLimit);
  const cashAdvanceOpenTotal = toNumber(cashAdvanceTotal);
  const payableBeforeCashAdvance =
    grossPayable + totalAdditions - expenseAmTotal - expenseZcTotal - savingsTotal;
  const cashAdvanceDeductionLimit = Math.max(0, payableBeforeCashAdvance);
  const cashAdvanceDeductionTotal = Math.min(
    cashAdvanceOpenTotal,
    cashAdvanceDeductionLimit
  );
  const cashAdvanceRemainingAfterBalancing = Math.max(
    0,
    cashAdvanceOpenTotal - cashAdvanceDeductionTotal
  );
  const totalDeductions =
    expenseAmTotal + expenseZcTotal + savingsTotal + cashAdvanceDeductionTotal;

  return {
    totalSacks,
    drySacks,
    rejectSacks,
    grossKilo,
    netKilo,
    dryKilo,
    wetShareKilo,
    wetBaseKilo,
    wetDeductionKilo,
    wetAdjustedKilo,
    rejectKilo,
    rejectAdjustedKilo,
    dryPay,
    wetPay,
    rejectPay,
    grossPayable,
    karotTotal,
    giveawaysDefault,
    giveawaysTotal,
    totalAdditions,
    savingsPotentialTotal,
    savingsTotal,
    expenseAmTotal,
    expenseZcTotal,
    cashAdvanceOpenTotal,
    cashAdvanceDeductionTotal,
    cashAdvanceRemainingAfterBalancing,
    totalDeductions,
    totalBalance: Math.max(0, grossPayable + totalAdditions - totalDeductions),
  };
}

function getPerPurchaseOrderPaymentTotals(purchaseOrders, totals) {
  const allocationLines = Array.isArray(totals.allocationLines) && totals.allocationLines.length
    ? totals.allocationLines
    : buildBalancingAllocationLines(purchaseOrders, {});
  const paymentBases = allocationLines
    .map((line) => ({
      sacks: toNumber(line.sacks),
      pricePerKilo: toNumber(line.pricePerKilo),
    }))
    .filter((basis) => basis.sacks > 0 && basis.pricePerKilo > 0);
  const totalSacks = paymentBases.reduce((total, basis) => total + basis.sacks, 0);

  if (!paymentBases.length || totalSacks <= 0) {
    return {
      dryPay: 0,
      wetPay: 0,
      rejectPay: 0,
      dryHasBasis: false,
      wetHasBasis: false,
      rejectHasBasis: false,
      hasBasis: false,
    };
  }

  const wetPaymentBases = Array.isArray(totals.wetPaymentBases)
    ? totals.wetPaymentBases.filter((basis) => basis.wetAdjustedKilo > 0 && basis.pricePerKilo > 0)
    : [];
  return {
    dryPay: paymentBases.reduce(
      (total, basis) =>
        total + getScaledKiloShare(basis.sacks, totalSacks, totals.dryKilo) * basis.pricePerKilo,
      0
    ),
    wetPay: wetPaymentBases.length
      ? wetPaymentBases.reduce(
          (total, basis) => total + basis.wetAdjustedKilo * basis.pricePerKilo,
          0
        )
      : paymentBases.reduce(
          (total, basis) =>
            total +
            getScaledKiloShare(basis.sacks, totalSacks, totals.wetAdjustedKilo) *
              basis.pricePerKilo,
          0
        ),
    rejectPay: paymentBases.reduce(
      (total, basis) =>
        total +
        getScaledKiloShare(basis.sacks, totalSacks, totals.rejectAdjustedKilo) * basis.pricePerKilo,
      0
    ),
    dryHasBasis: totals.dryKilo > 0,
    wetHasBasis: totals.wetAdjustedKilo > 0,
    rejectHasBasis: totals.rejectAdjustedKilo > 0,
    hasBasis: true,
  };
}

function getPerPurchaseOrderWetAdjustmentTotals(purchaseOrders, options) {
  const allocationLines = Array.isArray(options.allocationLines) && options.allocationLines.length
    ? options.allocationLines
    : buildBalancingAllocationLines(purchaseOrders, {});
  const paymentBases = allocationLines
    .map((line) => ({
      moistureContent: toNumber(line.sourceMoistureContent),
      pricePerKilo: toNumber(line.pricePerKilo),
      sacks: toNumber(line.sacks),
    }))
    .filter((basis) => basis.sacks > 0);
  const totalSacks = paymentBases.reduce((total, basis) => total + basis.sacks, 0);

  if (!paymentBases.length || totalSacks <= 0 || toNumber(options.wetBaseKilo) <= 0) {
    return {
      hasBasis: false,
      paymentBases: [],
      wetAdjustedKilo: toNumber(options.wetBaseKilo),
      wetDeductionKilo: 0,
    };
  }

  const bases = paymentBases.map((basis) => {
    const wetBaseKilo = getScaledKiloShare(basis.sacks, totalSacks, options.wetBaseKilo);
    const wetDeductionKilo = wetBaseKilo * (toNumber(basis.moistureContent) / 100);
    const wetAdjustedKilo = Math.max(0, wetBaseKilo - wetDeductionKilo);

    return {
      ...basis,
      wetAdjustedKilo,
      wetDeductionKilo,
    };
  });

  return {
    hasBasis: true,
    paymentBases: bases,
    wetAdjustedKilo: bases.reduce((total, basis) => total + basis.wetAdjustedKilo, 0),
    wetDeductionKilo: bases.reduce((total, basis) => total + basis.wetDeductionKilo, 0),
  };
}

function getScaledKiloShare(sourceKilo, sourceTotalKilo, targetTotalKilo) {
  if (sourceKilo <= 0 || sourceTotalKilo <= 0 || targetTotalKilo <= 0) {
    return 0;
  }

  return (sourceKilo / sourceTotalKilo) * targetTotalKilo;
}

function getComputedBalancingInputs(purchaseOrders) {
  const totalSacks = purchaseOrders.reduce(
    (total, purchaseOrder) => total + getPurchaseOrderSacks(purchaseOrder),
    0
  );
  const receivingItems = purchaseOrders.flatMap((purchaseOrder) =>
    Array.isArray(purchaseOrder.receivingItems) ? purchaseOrder.receivingItems : []
  );
  const categorizedItems = receivingItems.filter((item) =>
    ['Dry', 'Wet', 'Reject'].includes(String(item.category || '').trim())
  );

  if (!categorizedItems.length) {
    return {
      pricePerKilo: getSelectedPurchaseOrderPrice(purchaseOrders),
      grossKilo: 0,
      drySacks: totalSacks,
      wetSacks: 0,
      rejectSacks: 0,
      moistureContent: getSelectedReceivingMoistureContent(receivingItems),
    };
  }

  return {
    pricePerKilo: getSelectedPurchaseOrderPrice(purchaseOrders),
    grossKilo: roundNumber(
      categorizedItems.reduce(
        (total, item) =>
          String(item.category || '').trim() === 'Reject'
            ? total
            : total + getReceivingNetKilo(item),
        0
      )
    ),
    drySacks: roundNumber(sumReceivingSacks(categorizedItems, 'Dry')),
    wetSacks: roundNumber(sumReceivingSacks(categorizedItems, 'Wet')),
    rejectSacks: roundNumber(sumReceivingRejectNetKilo(categorizedItems)),
    moistureContent: getSelectedReceivingMoistureContent(receivingItems),
  };
}

function getSelectedReceivingMoistureContent(items) {
  const weightedMoisture = items.reduce(
    (totals, item) => {
      const moistureContent = parseMoistureContent(item.moistureContent);

      if (moistureContent <= 0) {
        return totals;
      }

      const sacks = getReceivingSacks(item);
      const weight = sacks > 0 ? sacks : 1;

      return {
        weightedTotal: totals.weightedTotal + moistureContent * weight,
        weightTotal: totals.weightTotal + weight,
      };
    },
    { weightedTotal: 0, weightTotal: 0 }
  );

  if (weightedMoisture.weightTotal <= 0) {
    return 0;
  }

  return roundNumber(weightedMoisture.weightedTotal / weightedMoisture.weightTotal);
}

function parseMoistureContent(value) {
  return toNumber(String(value || '').replace(/%/g, '').trim());
}

function getPurchaseOrderMoistureContent(purchaseOrder) {
  const receivingItems = Array.isArray(purchaseOrder.receivingItems)
    ? purchaseOrder.receivingItems
    : [];

  return getSelectedReceivingMoistureContent(receivingItems);
}

function sumReceivingSacks(items, category) {
  return items
    .filter((item) => String(item.category || '').trim() === category)
    .reduce((total, item) => total + getReceivingSacks(item), 0);
}

function sumReceivingRejectNetKilo(items) {
  return items
    .filter((item) => String(item.category || '').trim() === 'Reject')
    .reduce((total, item) => total + getReceivingNetKilo(item), 0);
}

function getReceivingSacks(item) {
  if (String(item.category || '').trim() === 'Reject') {
    return 0;
  }

  if (item.quantityUnit === 'sacks') {
    return toNumber(item.quantity);
  }

  return 0;
}

function getReceivingNetKilo(item) {
  return (
    toNumber(item.adjustedNetKg) ||
    toNumber(item.quantityKg) ||
    toNumber(item.rawNetKg)
  );
}

function getSelectedPurchaseOrderPrice(purchaseOrders) {
  const prices = purchaseOrders
    .map(getPurchaseOrderPrice)
    .filter((price) => price > 0);

  if (!prices.length) {
    return 0;
  }

  const firstPrice = prices[0];
  const hasSamePrice = prices.every((price) => price === firstPrice);

  if (hasSamePrice) {
    return firstPrice;
  }

  return roundNumber(prices.reduce((total, price) => total + price, 0) / prices.length);
}

function getPurchaseOrderPrice(purchaseOrder) {
  return toNumber(purchaseOrder?.actualPrice ?? purchaseOrder?.price);
}

function getPurchaseOrderSacks(purchaseOrder) {
  const quantity = Number(purchaseOrder.receivedQuantity ?? purchaseOrder.quantity ?? 0);

  return Number.isFinite(quantity) ? quantity : 0;
}

function getPurchaseOrderNetKilo(purchaseOrder) {
  const receivingItems = Array.isArray(purchaseOrder?.receivingItems)
    ? purchaseOrder.receivingItems
    : [];
  const netKilo = receivingItems
    .filter((item) => String(item.category || '').trim() !== 'Reject')
    .reduce((total, item) => total + getReceivingNetKilo(item), 0);

  return roundNumber(netKilo);
}

function getPurchaseOrderLansaName(purchaseOrder) {
  return (
    String(
      purchaseOrder?.lansaName ||
        purchaseOrder?.lansa_name ||
        purchaseOrder?.lansa ||
        purchaseOrder?.lansaDetails?.name ||
        purchaseOrder?.lansaDetails?.lansaName ||
        purchaseOrder?.lansaRecord?.name ||
        purchaseOrder?.lansaRecord?.lansaName ||
        ''
    ).trim() || '-'
  );
}

function getInputNumber(value, fallbackValue) {
  const normalizedValue = String(value ?? '').trim();

  if (normalizedValue === '') {
    return toNumber(fallbackValue);
  }

  return toNumber(normalizedValue);
}

function getInputDisplayValue(value, fallbackValue) {
  if (String(value ?? '').trim() !== '') {
    return value;
  }

  if (toNumber(fallbackValue) <= 0) {
    return '';
  }

  return formatEditableNumber(fallbackValue);
}

function getAutoInputNumber(name, values, fallbackValue, manualAutoFields) {
  if (manualAutoFields[name]) {
    return toNumber(values[name]);
  }

  return getInputNumber(values[name], fallbackValue);
}

function getAutoInputDisplayValue(name, values, fallbackValue, manualAutoFields) {
  if (manualAutoFields[name]) {
    return values[name];
  }

  return getInputDisplayValue(values[name], fallbackValue);
}

function getTarePaymentFallback(values, tareAmountFee) {
  return values.supplierId ? tareAmountFee : 0;
}

function sanitizeBalancingValue(name, value) {
  if (['supplierId', 'dateFrom', 'dateTo'].includes(name)) {
    return value;
  }

  const normalizedValue = String(value || '').replace(/[^\d.]/g, '');
  const [integerPart, ...decimalParts] = normalizedValue.split('.');

  return decimalParts.length
    ? `${integerPart}.${decimalParts.join('').slice(0, 2)}`
    : integerPart;
}

function sanitizeAllocationSacksValue(value, maxSacks = 0) {
  const normalizedValue = String(value || '')
    .replace(/,/g, '.')
    .replace(/[^\d.]/g, '');
  const [integerPart, ...decimalParts] = normalizedValue.split('.');
  const decimalPart = decimalParts.join('').slice(0, 2);
  const cleanValue = decimalParts.length ? `${integerPart}.${decimalPart}` : integerPart;

  if (!cleanValue) {
    return '';
  }

  const numericValue = Number(cleanValue);
  const normalizedMaxSacks = Number(maxSacks || 0);

  if (
    Number.isFinite(numericValue) &&
    Number.isFinite(normalizedMaxSacks) &&
    normalizedMaxSacks > 0 &&
    numericValue > normalizedMaxSacks
  ) {
    return formatEditableNumber(normalizedMaxSacks);
  }

  return cleanValue;
}

function toNumber(value) {
  const numericValue = Number(value || 0);

  return Number.isFinite(numericValue) ? numericValue : 0;
}

function shouldShowRemainingCashAdvance(totals) {
  return toNumber(totals?.cashAdvanceRemainingAfterBalancing) > 0;
}

function roundNumber(value) {
  return Math.round(Number(value || 0) * 100) / 100;
}

function formatEditableNumber(value) {
  const roundedValue = roundNumber(value);

  return Number.isInteger(roundedValue) ? String(roundedValue) : String(roundedValue);
}

function formatDate(value) {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function toDateInputValue(value) {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function formatPurchaseOrderCode(purchaseOrder) {
  return purchaseOrder.poCode || `PO-${String(purchaseOrder.id).padStart(4, '0')}`;
}

function formatBalancingDateRange(dateFrom, dateTo) {
  if (dateFrom && dateTo) {
    return `${formatDate(dateFrom)} - ${formatDate(dateTo)}`;
  }

  return formatDate(dateFrom || dateTo);
}

function formatTransactedBy(record, currentUser) {
  const currentUserId = normalizeIdentityValue(currentUser?.id);
  const transactedByUserId = normalizeIdentityValue(record.createdByUserId);

  if (currentUserId && transactedByUserId && currentUserId === transactedByUserId) {
    return 'You';
  }

  return record.createdByName || 'Admin';
}

function formatPdfTransactedBy(record) {
  return record.createdByName || 'Admin';
}

function normalizeIdentityValue(value) {
  return String(value ?? '').trim();
}

function downloadSupplierBalancingPdf(record, transactedBy, purchaseOrders = []) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 46;
  const contentWidth = pageWidth - margin * 2;
  const totals = record.totals || {};
  const inputs = record.inputs || {};
  const colors = getPdfThemeColors();
  const supplierName = getSupplierBalancingPdfHeaderName(record);
  const filename = `${getSupplierBalancingPdfFileName(record)}.pdf`;
  const includedOrderRows = getSupplierBalancingIncludedOrderRows(
    purchaseOrders,
    totals,
    inputs.balancingAllocations
  );
  const paymentRows = [
    ['Dry Pay', formatPdfCurrency(totals.dryPay)],
    ['Wet Pay', formatPdfCurrency(totals.wetPay)],
    ['Reject Pay', formatPdfCurrency(totals.rejectPay)],
    ['Gross Payable', formatPdfCurrency(totals.grossPayable), 'success'],
    ['Giveaways (owner add-on)', formatPdfCurrency(totals.giveawaysTotal)],
    ['Karot (owner add-on)', formatPdfCurrency(totals.karotTotal)],
    ['Expense AM', formatPdfCurrency(totals.expenseAmTotal)],
    ['Expense ZC', formatPdfCurrency(totals.expenseZcTotal)],
    ['Supplier Savings', formatPdfCurrency(totals.savingsTotal)],
    ['Cash Advance', formatPdfCurrency(totals.cashAdvanceDeductionTotal)],
    ...(shouldShowRemainingCashAdvance(totals)
      ? [['Remaining C.A.', formatPdfCurrency(totals.cashAdvanceRemainingAfterBalancing)]]
      : []),
    ['Total Deductions', formatPdfCurrency(totals.totalDeductions), 'warning'],
    ['Total Balance', formatPdfCurrency(totals.totalBalance), 'final'],
  ];
  let y = 34;

  doc.setFillColor(...colors.accent);
  doc.roundedRect(margin, y, contentWidth, 82, 14, 14, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('SUPPLIER BALANCING STATEMENT', margin + 18, y + 24);
  doc.setFontSize(22);
  doc.text(supplierName, margin + 18, y + 50);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`Reference No. ${formatSupplierBalancingReference(record.id)}`, margin + 18, y + 68);

  doc.setDrawColor(255, 255, 255);
  doc.setFillColor(...colors.accentStrong);
  doc.roundedRect(pageWidth - margin - 168, y + 17, 144, 48, 11, 11, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.text('TOTAL BALANCE', pageWidth - margin - 96, y + 34, { align: 'center' });
  doc.setFontSize(16);
  drawFittedPdfText(
    doc,
    formatPdfCurrency(totals.totalBalance),
    pageWidth - margin - 96,
    y + 55,
    122,
    16,
    { align: 'center' }
  );

  y += 94;
  drawPdfMetaGrid(doc, colors, margin, y, contentWidth, [
    ['Created', formatDate(record.createdAt)],
    ['Transacted By', transactedBy || '-'],
    ['Status', record.status || 'For Payment'],
    ['Date Range', formatBalancingDateRange(record.dateFrom, record.dateTo)],
  ], { cardHeight: 42, labelSize: 6.8, valueSize: 10.2, gap: 8 });

  y += 50;
  drawPdfMetaGrid(doc, colors, margin, y, contentWidth, [
    ['Included Orders', formatNumber(record.purchaseOrderIds?.length || 0, 0)],
    ['Total Sacks', formatNumber(totals.totalSacks, 0)],
    ['Price Basis', includedOrderRows.length ? 'Per P.O.' : formatPdfCurrency(inputs.pricePerKilo)],
    ['Gross Kilo', `${formatNumber(totals.grossKilo)} kg`],
  ], { cardHeight: 42, labelSize: 6.8, valueSize: 10.2, gap: 8 });

  y += 56;
  y = drawPdfSectionTitle(doc, colors, margin, y, 'Included P.O. Details');
  y = drawPdfIncludedOrdersTable(doc, colors, margin, y, contentWidth, includedOrderRows);

  y += 16;
  y = drawPdfSectionTitle(doc, colors, margin, y, 'Weight Breakdown');
  y = drawPdfTwoColumnTable(doc, colors, margin, y, contentWidth, [
    ['Net Weight', `${formatNumber(totals.netKilo)} kg`, 'Dry Weight', `${formatNumber(totals.dryKilo)} kg`],
    ['Wet Adjusted', `${formatNumber(totals.wetAdjustedKilo)} kg`, 'Reject Adjusted', `${formatNumber(totals.rejectAdjustedKilo)} kg`],
    ['Wet Deduction', `${formatNumber(totals.wetDeductionKilo)} kg`, 'Cash Advance Applied', formatPdfCurrency(totals.cashAdvanceDeductionTotal)],
  ]);

  y += 14;
  if (y + getPdfPaymentSectionMinimumHeight() > pageHeight - margin) {
    doc.addPage();
    y = margin;
  }
  y = drawPdfSectionTitle(doc, colors, margin, y, 'Payment Computation');
  drawPaginatedPdfAmountTable(doc, colors, margin, y, contentWidth, paymentRows, {
    pageHeight,
    margin,
  });

  try {
    doc.save(filename);
  } catch (error) {
    printSupplierBalancingRecord(record, transactedBy, purchaseOrders);
  }
}

function printSupplierBalancingRecord(record, transactedBy, purchaseOrders = []) {
  const printDocument = buildSupplierBalancingPrintHtml(record, transactedBy, purchaseOrders);
  const printFileName = getSupplierBalancingPdfFileName(record);
  const printRoot = document.createElement('div');
  const printStyle = document.createElement('style');
  const originalTitle = document.title;
  const printRootId = `supplier-balancing-print-${Date.now()}`;

  printRoot.id = printRootId;
  printRoot.innerHTML = extractPrintBody(printDocument);
  printStyle.textContent = `
    ${extractPrintStyle(printDocument)}

    @media screen {
      #${printRootId} {
        display: none !important;
      }
    }

    @media print {
      body > *:not(#${printRootId}) {
        display: none !important;
      }

      #${printRootId} {
        display: block !important;
      }
    }
  `;

  function cleanupPrintNodes() {
    document.title = originalTitle;
    printRoot.remove();
    printStyle.remove();
    window.removeEventListener('afterprint', cleanupPrintNodes);
  }

  document.title = printFileName;
  document.head.appendChild(printStyle);
  document.body.appendChild(printRoot);
  window.addEventListener('afterprint', cleanupPrintNodes);

  window.setTimeout(() => {
    window.print();
  }, 100);
}

function buildSupplierBalancingPrintHtml(record, transactedBy, purchaseOrders = []) {
  const totals = record.totals || {};
  const inputs = record.inputs || {};
  const supplierName = getSupplierBalancingPdfHeaderName(record);
  const title = getSupplierBalancingPdfFileName(record);
  const status = record.status || 'For Payment';
  const includedOrderRows = getSupplierBalancingIncludedOrderRows(
    purchaseOrders,
    totals,
    inputs.balancingAllocations
  );
  const remainingCashAdvanceRow = shouldShowRemainingCashAdvance(totals)
    ? printAmountRow('Remaining C.A.', formatCurrency(totals.cashAdvanceRemainingAfterBalancing))
    : '';

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <style>
      * { box-sizing: border-box; }
      body {
        margin: 0;
        padding: 24px;
        color: var(--theme-text, #173236);
        font-family: Arial, Helvetica, sans-serif;
        background: var(--theme-surface-warm, #eef4f3);
      }
      .sheet {
        --pdf-accent: var(--theme-accent, #6f9392);
        --pdf-accent-strong: var(--theme-accent-strong, #315f61);
        --pdf-accent-soft: var(--theme-accent-soft, #eef8ef);
        --pdf-accent-panel: var(--theme-accent-panel, #dbe9e8);
        --pdf-accent-panel-text: var(--theme-accent-panel-text, #42656a);
        --pdf-border: var(--theme-border, #d8e5e5);
        --pdf-border-soft: var(--theme-border-soft, #e5eeee);
        --pdf-text: var(--theme-text, #173236);
        --pdf-text-strong: var(--theme-text-strong, #10282b);
        --pdf-text-muted: var(--theme-text-muted, #587579);
        --pdf-surface-muted: var(--theme-surface-muted, #f8fbfb);
        --pdf-surface-soft: var(--theme-surface-soft, #f0f8f1);
        max-width: 820px;
        margin: 0 auto;
        overflow: hidden;
        border: 1px solid var(--pdf-border);
        border-radius: 22px;
        background: #ffffff;
        box-shadow: 0 18px 42px rgba(35, 62, 64, 0.12);
      }
      .header {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        gap: 12px;
        padding: 18px 24px;
        color: #ffffff;
        background: linear-gradient(135deg, var(--pdf-accent) 0%, var(--pdf-accent-strong) 100%);
      }
      .eyebrow {
        margin: 0;
        color: rgba(255, 255, 255, 0.78);
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }
      h1 {
        margin: 5px 0 0;
        color: #ffffff;
        font-size: 24px;
        line-height: 1.1;
      }
      .subtitle {
        margin: 5px 0 0;
        color: rgba(255, 255, 255, 0.82);
        font-size: 13px;
      }
      .balance-card {
        min-width: 176px;
        border: 1px solid rgba(255, 255, 255, 0.28);
        border-radius: 15px;
        padding: 11px 12px;
        background: rgba(255, 255, 255, 0.14);
        text-align: right;
      }
      .balance-card span {
        display: block;
        color: rgba(255, 255, 255, 0.78);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }
      .balance-card strong {
        display: block;
        margin-top: 6px;
        color: #ffffff;
        font-size: 21px;
        line-height: 1;
      }
      .content {
        padding: 16px 24px 22px;
      }
      .meta-grid {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 8px;
        margin-bottom: 16px;
      }
      .meta {
        min-height: 46px;
        border: 1px solid var(--pdf-border);
        border-radius: 14px;
        padding: 7px 9px;
        background: var(--pdf-surface-muted);
      }
      .meta .label,
      .metric .label {
        display: block;
        color: var(--pdf-text-muted);
        font-size: 8.5px;
        font-weight: 800;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }
      .meta .value,
      .metric .value {
        display: block;
        margin-top: 3px;
        color: var(--pdf-text-strong);
        font-size: 11px;
        font-weight: 800;
      }
      .metrics {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 8px;
      }
      .metric {
        border: 1px solid var(--pdf-border);
        border-radius: 14px;
        padding: 8px 9px;
        background: #ffffff;
      }
      h2 {
        margin: 14px 0 4px;
        color: var(--pdf-accent-panel-text);
        font-size: 12px;
        letter-spacing: 0.1em;
        text-transform: uppercase;
      }
      h2.included-orders-title {
        margin-top: 18px;
      }
      table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        overflow: hidden;
        border: 1px solid var(--pdf-border);
        border-radius: 14px;
        font-size: 12px;
      }
      th {
        padding: 8px 10px;
        color: var(--pdf-accent-panel-text);
        background: var(--pdf-accent-panel);
        font-size: 9px;
        font-weight: 800;
        letter-spacing: 0.08em;
        text-align: left;
        text-transform: uppercase;
      }
      td {
        padding: 8px 10px;
        border-top: 1px solid var(--pdf-border-soft);
        color: var(--pdf-text);
      }
      td.amount,
      th.amount {
        text-align: right;
      }
      tr.highlight td {
        background: var(--pdf-surface-soft);
        font-weight: 800;
      }
      tr.warning td {
        background: var(--pdf-accent-soft);
        font-weight: 800;
      }
      tr.final td {
        border-top: 2px solid var(--pdf-border);
        background: var(--pdf-surface-muted);
        font-size: 17px;
        font-weight: 900;
      }
      .status-pill {
        display: inline-flex;
        border-radius: 999px;
        padding: 5px 10px;
        color: var(--pdf-accent-strong);
        background: var(--pdf-accent-soft);
        font-size: 11px;
        font-weight: 800;
      }
      .footer-note {
        margin-top: 22px;
        color: var(--pdf-text-muted);
        font-size: 11px;
        text-align: center;
      }
      @page {
        size: A4 portrait;
        margin: 18mm;
      }
      @media print {
        body {
          padding: 0;
          background: #ffffff;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .sheet {
          border: 0;
          border-radius: 0;
          box-shadow: none;
        }
      }
    </style>
  </head>
  <body>
    <main class="sheet">
      <section class="header">
        <div>
          <p class="eyebrow">Supplier Balancing Statement</p>
          <h1>${escapeHtml(supplierName)}</h1>
          <p class="subtitle">Reference No. ${escapeHtml(formatSupplierBalancingReference(record.id))}</p>
        </div>
        <div class="balance-card">
          <span>Total Balance</span>
          <strong>${escapeHtml(formatCurrency(totals.totalBalance))}</strong>
        </div>
      </section>

      <div class="content">
        <section class="meta-grid">
          ${printMeta('Created', formatDate(record.createdAt))}
          ${printMeta('Transacted By', transactedBy || '-')}
          ${printMeta('Status', `<span class="status-pill">${escapeHtml(status)}</span>`, true)}
          ${printMeta('Date Range', formatBalancingDateRange(record.dateFrom, record.dateTo))}
        </section>

        <section class="metrics">
          ${printMetric('Included Orders', formatNumber(record.purchaseOrderIds?.length || 0, 0))}
          ${printMetric('Total Sacks', formatNumber(totals.totalSacks, 0))}
          ${printMetric('Price Basis', includedOrderRows.length ? 'Per P.O.' : formatCurrency(inputs.pricePerKilo))}
          ${printMetric('Gross Kilo', `${formatNumber(totals.grossKilo)} kg`)}
        </section>

        <h2 class="included-orders-title">Included P.O. Details</h2>
        <table class="included-orders-table">
          <colgroup>
            <col style="width: 15%;">
            <col style="width: 42%;">
            <col style="width: 12%;">
            <col style="width: 13%;">
            <col style="width: 18%;">
          </colgroup>
          <thead>
            <tr>
              <th>P.O. Date</th>
              <th>Lansa Name</th>
              <th>Type</th>
              <th class="amount">Sacks</th>
              <th class="amount">KG Net</th>
            </tr>
          </thead>
          <tbody>
            ${printIncludedOrderRows(includedOrderRows)}
          </tbody>
        </table>

        <h2>Weight Breakdown</h2>
        <table>
          <thead>
            <tr>
              <th>Metric</th>
              <th class="amount">Value</th>
              <th>Metric</th>
              <th class="amount">Value</th>
            </tr>
          </thead>
          <tbody>
            ${printPairedRow('Net Weight', `${formatNumber(totals.netKilo)} kg`, 'Dry Weight', `${formatNumber(totals.dryKilo)} kg`)}
            ${printPairedRow('Wet Adjusted', `${formatNumber(totals.wetAdjustedKilo)} kg`, 'Reject Adjusted', `${formatNumber(totals.rejectAdjustedKilo)} kg`)}
            ${printPairedRow('Wet Deduction', `${formatNumber(totals.wetDeductionKilo)} kg`, 'Cash Advance Applied', formatCurrency(totals.cashAdvanceDeductionTotal))}
          </tbody>
        </table>

        <h2>Payment Computation</h2>
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th class="amount">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${printAmountRow('Dry Pay', formatCurrency(totals.dryPay))}
            ${printAmountRow('Wet Pay', formatCurrency(totals.wetPay))}
            ${printAmountRow('Reject Pay', formatCurrency(totals.rejectPay))}
            ${printAmountRow('Gross Payable', formatCurrency(totals.grossPayable), 'highlight')}
            ${printAmountRow('Giveaways (owner add-on)', formatCurrency(totals.giveawaysTotal))}
            ${printAmountRow('Karot (owner add-on)', formatCurrency(totals.karotTotal))}
            ${printAmountRow('Expense AM', formatCurrency(totals.expenseAmTotal))}
            ${printAmountRow('Expense ZC', formatCurrency(totals.expenseZcTotal))}
            ${printAmountRow('Supplier Savings', formatCurrency(totals.savingsTotal))}
            ${printAmountRow('Cash Advance', formatCurrency(totals.cashAdvanceDeductionTotal))}
            ${remainingCashAdvanceRow}
            ${printAmountRow('Total Deductions', formatCurrency(totals.totalDeductions), 'warning')}
            ${printAmountRow('Total Balance', formatCurrency(totals.totalBalance), 'final')}
          </tbody>
        </table>

        <p class="footer-note">Generated from Seaweeds supplier balancing records.</p>
      </div>
    </main>
  </body>
</html>`;
}

function printMeta(label, value, isHtml = false) {
  return `<div class="meta"><span class="label">${escapeHtml(label)}</span><span class="value">${isHtml ? value : escapeHtml(value)}</span></div>`;
}

function printMetric(label, value) {
  return `<div class="metric"><span class="label">${escapeHtml(label)}</span><span class="value">${escapeHtml(value)}</span></div>`;
}

function printPairedRow(firstLabel, firstValue, secondLabel, secondValue) {
  return `<tr><td>${escapeHtml(firstLabel)}</td><td class="amount">${escapeHtml(firstValue)}</td><td>${escapeHtml(secondLabel)}</td><td class="amount">${escapeHtml(secondValue)}</td></tr>`;
}

function printIncludedOrderRows(rows) {
  if (!rows.length) {
    return '<tr><td colspan="5">No included P.O. details found.</td></tr>';
  }

  return rows
    .map(
      (row) => `<tr>
        <td>${escapeHtml(row.date)}</td>
        <td>${escapeHtml(row.lansaName)}</td>
        <td>${escapeHtml(row.type)}</td>
        <td class="amount">${escapeHtml(formatIncludedOrderSacks(row))}</td>
        <td class="amount">${escapeHtml(`${formatNumber(row.netKg)} kg`)}</td>
      </tr>`
    )
    .join('');
}

function printAmountRow(label, value, className = '') {
  return `<tr class="${escapeHtml(className)}"><td>${escapeHtml(label)}</td><td class="amount">${escapeHtml(value)}</td></tr>`;
}

function extractPrintBody(printDocument) {
  const bodyMatch = String(printDocument || '').match(/<body[^>]*>([\s\S]*)<\/body>/i);

  return bodyMatch ? bodyMatch[1] : '';
}

function extractPrintStyle(printDocument) {
  const styleMatch = String(printDocument || '').match(/<style[^>]*>([\s\S]*)<\/style>/i);

  return styleMatch ? styleMatch[1] : '';
}

function drawPdfMetaGrid(doc, colors, x, y, width, items, options = {}) {
  const gap = options.gap ?? 10;
  const cardHeight = options.cardHeight ?? 58;
  const labelSize = options.labelSize ?? 8;
  const valueSize = options.valueSize ?? 12;
  const boxWidth = (width - gap * 3) / 4;

  items.forEach(([label, value], index) => {
    const itemX = x + index * (boxWidth + gap);

    doc.setDrawColor(...colors.border);
    doc.setFillColor(...colors.surfaceMuted);
    doc.roundedRect(itemX, y, boxWidth, cardHeight, 10, 10, 'FD');
    doc.setTextColor(...colors.textMuted);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(labelSize);
    doc.text(String(label).toUpperCase(), itemX + 10, y + 15);
    doc.setTextColor(...colors.textStrong);
    doc.setFontSize(valueSize);
    drawFittedPdfText(doc, String(value || '-'), itemX + 10, y + 31, boxWidth - 20, valueSize);
  });
}

function drawPdfSectionTitle(doc, colors, x, y, title) {
  doc.setTextColor(...colors.accentPanelText);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(String(title).toUpperCase(), x, y);

  return y + 8;
}

function drawPdfTopRoundedRect(doc, x, y, width, height, radius, style) {
  doc.roundedRect(x, y, width, height, radius, radius, style);
  doc.rect(x, y + height - radius, width, radius, style);
}

function drawPdfBottomRoundedRect(doc, x, y, width, height, radius, style) {
  doc.roundedRect(x, y, width, height, radius, radius, style);
  doc.rect(x, y, width, radius, style);
}

function drawPdfTwoColumnTable(doc, colors, x, y, width, rows) {
  const rowHeight = 24;
  const columnWidth = width / 4;

  doc.setFillColor(...colors.accentPanel);
  doc.setDrawColor(...colors.border);
  drawPdfTopRoundedRect(doc, x, y, width, rowHeight, 11, 'F');
  ['Metric', 'Value', 'Metric', 'Value'].forEach((heading, index) => {
    doc.setTextColor(...colors.accentPanelText);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text(heading.toUpperCase(), x + columnWidth * index + 10, y + 16);
  });

  rows.forEach((row, rowIndex) => {
    const rowY = y + rowHeight * (rowIndex + 1);

    doc.setFillColor(255, 255, 255);
    doc.rect(x, rowY, width, rowHeight, 'F');
    doc.setDrawColor(...colors.borderSoft);
    doc.line(x, rowY, x + width, rowY);
    row.forEach((value, index) => {
      const isAmount = index % 2 === 1;

      doc.setTextColor(...colors.text);
      doc.setFont('helvetica', isAmount ? 'bold' : 'normal');
      doc.setFontSize(9);
      drawFittedPdfText(
        doc,
        String(value || '-'),
        x + columnWidth * index + 10,
        rowY + 16,
        columnWidth - 20,
        9
      );
    });
  });

  doc.setDrawColor(...colors.border);
  doc.roundedRect(x, y, width, rowHeight * (rows.length + 1), 11, 11, 'S');

  return y + rowHeight * (rows.length + 1);
}

function drawPdfIncludedOrdersTable(doc, colors, x, y, width, rows) {
  const headerHeight = 21;
  const rowHeight = 20;
  const lansaNameWidth = 194;
  const columns = [
    { heading: 'P.O. Date', width: 78 },
    { heading: 'Lansa Name', width: lansaNameWidth },
    { heading: 'Type', width: 58 },
    { heading: 'Sacks', width: 54, align: 'right' },
    { heading: 'KG Net', width: width - 78 - lansaNameWidth - 58 - 54, align: 'right' },
  ];
  const tableRows = rows.length
    ? rows
    : [{ date: '-', lansaName: 'No included P.O. details found.', type: '-', sacks: 0, price: 0, netKg: 0 }];

  doc.setFillColor(...colors.accentPanel);
  doc.setDrawColor(...colors.border);
  drawPdfTopRoundedRect(doc, x, y, width, headerHeight, 10, 'F');

  let cursorX = x;
  columns.forEach((column) => {
    doc.setTextColor(...colors.accentPanelText);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text(
      column.heading.toUpperCase(),
      column.align === 'right' ? cursorX + column.width - 8 : cursorX + 8,
      y + 14,
      column.align === 'right' ? { align: 'right' } : undefined
    );
    cursorX += column.width;
  });

  tableRows.forEach((row, rowIndex) => {
    const rowY = y + headerHeight + rowHeight * rowIndex;
    const values = [
      row.date,
      row.lansaName,
      row.type,
      formatIncludedOrderSacks(row),
      `${formatNumber(row.netKg)} kg`,
    ];

    doc.setFillColor(255, 255, 255);
    doc.rect(x, rowY, width, rowHeight, 'F');
    doc.setDrawColor(...colors.borderSoft);
    doc.line(x, rowY, x + width, rowY);
    doc.setTextColor(...colors.text);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.2);

    cursorX = x;
    columns.forEach((column, columnIndex) => {
      const textX = column.align === 'right' ? cursorX + column.width - 8 : cursorX + 8;
      drawFittedPdfText(
        doc,
        values[columnIndex],
        textX,
        rowY + 13,
        column.width - 14,
        8.2,
        column.align === 'right' ? { align: 'right' } : {}
      );
      cursorX += column.width;
    });
  });

  doc.setDrawColor(...colors.border);
  doc.roundedRect(x, y, width, headerHeight + rowHeight * tableRows.length, 10, 10, 'S');

  return y + headerHeight + rowHeight * tableRows.length;
}

function drawPaginatedPdfAmountTable(doc, colors, x, y, width, rows, options = {}) {
  const rowHeight = 19;
  const finalRowHeight = 25;
  const bottomY = (options.pageHeight || doc.internal.pageSize.getHeight()) - (options.margin || 46);
  let cursorY = y;
  let segmentStartY = y;
  let segmentHasRows = false;

  function drawHeader(headerY) {
    const amountX = x + width - 12;

    doc.setFillColor(...colors.accentPanel);
    doc.setDrawColor(...colors.border);
    drawPdfTopRoundedRect(doc, x, headerY, width, rowHeight, 11, 'F');
    doc.setTextColor(...colors.accentPanelText);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('DESCRIPTION', x + 12, headerY + 13);
    doc.text('AMOUNT', amountX, headerY + 13, { align: 'right' });

    return headerY + rowHeight;
  }

  function drawSegmentBorder() {
    if (!segmentHasRows) {
      return;
    }

    doc.setDrawColor(...colors.border);
    doc.roundedRect(x, segmentStartY, width, cursorY - segmentStartY, 11, 11, 'S');
  }

  cursorY = drawHeader(cursorY);

  rows.forEach(([label, amount, tone]) => {
    const isFinal = tone === 'final';
    const currentRowHeight = isFinal ? finalRowHeight : rowHeight;

    if (cursorY + currentRowHeight > bottomY && segmentHasRows) {
      drawSegmentBorder();
      doc.addPage();
      cursorY = options.margin || 46;
      segmentStartY = cursorY;
      segmentHasRows = false;
      cursorY = drawHeader(cursorY);
    }

    drawPdfAmountTableRow(doc, colors, x, cursorY, width, [label, amount, tone], currentRowHeight);
    cursorY += currentRowHeight;
    segmentHasRows = true;
  });

  drawSegmentBorder();

  return cursorY;
}

function drawPdfAmountTableRow(doc, colors, x, y, width, row, rowHeight) {
  const amountX = x + width - 12;
  const [label, amount, tone] = row;
  const isFinal = tone === 'final';
  const isStrong = tone === 'success' || tone === 'warning' || isFinal;

  if (tone === 'success') {
    doc.setFillColor(...colors.surfaceSoft);
  } else if (tone === 'warning') {
    doc.setFillColor(...colors.accentSoft);
  } else if (isFinal) {
    doc.setFillColor(...colors.surfaceMuted);
  } else {
    doc.setFillColor(255, 255, 255);
  }

  if (isFinal) {
    drawPdfBottomRoundedRect(doc, x, y, width, rowHeight, 11, 'F');
  } else {
    doc.rect(x, y, width, rowHeight, 'F');
  }
  doc.setDrawColor(...colors.borderSoft);
  doc.line(x, y, x + width, y);
  doc.setTextColor(...colors.text);
  doc.setFont('helvetica', isStrong ? 'bold' : 'normal');
  doc.setFontSize(isFinal ? 12 : 8.8);
  doc.text(String(label), x + 12, y + (isFinal ? 17 : 13));
  drawFittedPdfText(
    doc,
    String(amount),
    amountX,
    y + (isFinal ? 17 : 13),
    width * 0.42,
    isFinal ? 12 : 8.8,
    { align: 'right' }
  );
}

function getPdfPaymentSectionMinimumHeight() {
  return 13 + 19 + 19;
}

function drawFittedPdfText(doc, text, x, y, maxWidth, initialFontSize, options = {}) {
  let fontSize = initialFontSize;
  const normalizedText = String(text || '-');

  while (fontSize > 6 && doc.getTextWidth(normalizedText) > maxWidth) {
    fontSize -= 0.5;
    doc.setFontSize(fontSize);
  }

  doc.text(normalizedText, x, y, options);
  doc.setFontSize(initialFontSize);
}

function getPdfThemeColors() {
  const rootStyle = getComputedStyle(document.documentElement);

  return {
    accent: cssColorToRgb(rootStyle.getPropertyValue('--theme-accent'), [111, 147, 146]),
    accentStrong: cssColorToRgb(rootStyle.getPropertyValue('--theme-accent-strong'), [49, 95, 97]),
    accentSoft: cssColorToRgb(rootStyle.getPropertyValue('--theme-accent-soft'), [238, 248, 239]),
    accentPanel: cssColorToRgb(rootStyle.getPropertyValue('--theme-accent-panel'), [219, 233, 232]),
    accentPanelText: cssColorToRgb(rootStyle.getPropertyValue('--theme-accent-panel-text'), [66, 101, 106]),
    border: cssColorToRgb(rootStyle.getPropertyValue('--theme-border'), [216, 229, 229]),
    borderSoft: cssColorToRgb(rootStyle.getPropertyValue('--theme-border-soft'), [229, 238, 238]),
    surfaceMuted: cssColorToRgb(rootStyle.getPropertyValue('--theme-surface-muted'), [248, 251, 251]),
    surfaceSoft: cssColorToRgb(rootStyle.getPropertyValue('--theme-surface-soft'), [240, 248, 241]),
    text: cssColorToRgb(rootStyle.getPropertyValue('--theme-text'), [23, 50, 54]),
    textStrong: cssColorToRgb(rootStyle.getPropertyValue('--theme-text-strong'), [16, 40, 43]),
    textMuted: cssColorToRgb(rootStyle.getPropertyValue('--theme-text-muted'), [88, 117, 121]),
  };
}

function formatPdfCurrency(value) {
  return `PHP ${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function cssColorToRgb(value, fallback) {
  const normalizedValue = String(value || '').trim();
  const hexMatch = normalizedValue.match(/^#([a-f\d]{3}|[a-f\d]{6})$/i);
  const rgbMatch = normalizedValue.match(/rgba?\(([^)]+)\)/i);

  if (hexMatch) {
    const hexValue = hexMatch[1].length === 3
      ? hexMatch[1].split('').map((char) => `${char}${char}`).join('')
      : hexMatch[1];

    return [
      parseInt(hexValue.slice(0, 2), 16),
      parseInt(hexValue.slice(2, 4), 16),
      parseInt(hexValue.slice(4, 6), 16),
    ];
  }

  if (rgbMatch) {
    const parts = rgbMatch[1]
      .split(',')
      .slice(0, 3)
      .map((part) => Number(part.trim()));

    if (parts.every((part) => Number.isFinite(part))) {
      return parts;
    }
  }

  return fallback;
}

function getSupplierBalancingPdfFileName(record) {
  const supplierName =
    String(record?.sacksMark || '').trim() ||
    String(record?.supplierName || '').trim() ||
    'Supplier';

  return `${sanitizePdfFileNamePart(supplierName)}-${getTodayDateStamp()}`;
}

function sanitizePdfFileNamePart(value) {
  return String(value || 'Supplier')
    .trim()
    .replace(/[<>:"/\\|?*]+/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'Supplier';
}

function getTodayDateStamp() {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  return `${month}${day}${year}`;
}

function getSupplierBalancingPdfHeaderName(record) {
  return String(record?.supplierName || '').trim() || String(record?.sacksMark || '').trim() || 'Supplier';
}

function formatSupplierBalancingReference(id) {
  const numericId = Number(id);

  if (!Number.isInteger(numericId) || numericId <= 0) {
    return 'BAL-0000';
  }

  return `BAL-${String(numericId).padStart(4, '0')}`;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function formatSupplierDisplay(sacksMark, supplierName) {
  const normalizedSacksMark = String(sacksMark || '').trim();
  const normalizedSupplierName = String(supplierName || '').trim();

  if (normalizedSacksMark && normalizedSupplierName) {
    return `${normalizedSacksMark} - ${normalizedSupplierName}`;
  }

  return normalizedSacksMark || normalizedSupplierName || '-';
}

function formatCount(value, singularLabel, pluralLabel = `${singularLabel}s`) {
  const numericValue = Number(value || 0);

  return `${numericValue.toLocaleString('en-US')} ${
    numericValue === 1 ? singularLabel : pluralLabel
  }`;
}

function formatNumber(value, maximumFractionDigits = 2) {
  return Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: maximumFractionDigits === 0 ? 0 : 2,
    maximumFractionDigits,
  });
}

function formatIncludedOrderSacks(row) {
  return String(row?.type || '').trim() === 'Reject' ? '-' : formatNumber(row?.sacks, 0);
}

function formatPercent(value) {
  const numericValue = toNumber(value);

  if (numericValue <= 0) {
    return '-';
  }

  return `${formatEditableNumber(numericValue)}%`;
}

function formatCurrency(value) {
  return Number(value || 0).toLocaleString('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function compareSavedBalancingRecords(firstRecord, secondRecord) {
  const firstStatusRank = getSavedBalancingStatusRank(firstRecord.status);
  const secondStatusRank = getSavedBalancingStatusRank(secondRecord.status);

  if (firstStatusRank !== secondStatusRank) {
    return firstStatusRank - secondStatusRank;
  }

  const firstCreatedTime = new Date(firstRecord.createdAt || 0).getTime();
  const secondCreatedTime = new Date(secondRecord.createdAt || 0).getTime();

  return secondCreatedTime - firstCreatedTime || Number(secondRecord.id || 0) - Number(firstRecord.id || 0);
}

function getSavedBalancingStatusRank(status) {
  return status === 'For Payment' ? 0 : 1;
}

export default PurchaseOrderSupplierBalancing;
